import type {
  AnalyticsResult,
  AnalyticsSearchQuery,
  AnalyticsSearchResults,
  ItemAnalyticsDto,
} from '@mimir/common/dto/analyticsModels';
import type {
  ItemActivityQueryParams,
  TenantSystemActivityQueryParams,
  SystemActivityResponseDto,
  ItemActivityResponseDto,
} from '@mimir/common/dto/systemActivityDtos';
import type { ClipListDto, ClipListUpdateDto } from '@mimir/common/dto/clipListDto';
import type { ArchiveOperationRequestDto, ArchiveOperationResponseDto } from '@mimir/common/dto/archiveOperationDto';
import type { TenantLogoDto, TenantLogoUploadDto } from '@mimir/common/dto/tenantLogoDto';
import type { ReprocessActionType } from '@mimir/common/dto/asyncOperationPrimitives';
import type { CreateTranscriptOptionDto, TenantOpsSummaryReport } from '@mimir/common/dto/asyncOperationModels';
import type GroupModel from '@mimir/common/dto/groupModel';
import type { UpdateGroupDto, CreateGroupDto } from '@mimir/common/dto/groupModel';
import type { IngestStorageConfigClientDto, UpdateUploadLocationDto } from '@mimir/common/dto/ingestStorageConfigDto';
import type { FeatureFlagDto, UpdateFeatureFlagDto } from '@mimir/common/dto/featureFlagDto';
import type { BookmarkDto, BookmarkFolderEntity, BookmarkFolderWithEtag, BookmarkWriteDto, CreateBookmarkDto, DeleteBookmarkFolderDto, UpdateBookmarkDto } from '@mimir/common/dto/bookmarkDto';
import type { QcEventsUpdateDto } from '@mimir/common/dto/qcDtos';
import type { NonScheduledLiveItemCreationRequestDto, ScheduledRecordingDto, GetScheduledRecordingsDto, ScheduledRecordingPutRequestDto, ScheduledRecordingCreationRequestDto, RecordingSeriesFullDto, RecordingSeriesCreationDto, RecordingSeriesUpdateDto, RecordingSeriesItemCreationDto } from '@mimir/common/dto/liveRecordingDtos';
import type { NonScheduledLiveItemCreationResponseDto } from '@mimir/common/dto/liveMetadataDtos';
import type {
  LiveItemStateRequestDto,
  LiveRecordingItemStateDto,
  PipelineActionResultDto,
} from '@mimir/common/dto/liveDtos';
import type { LoadedItem, ItemUpdateRequestDto } from '@mimir/common/dto/searchResultHit';
import type { ItemCreationRequestDto } from '@mimir/common/dto/itemCreationRequestDto';
import type { ExtendedItemDto, ItemCustomIconUpdateDto } from '@mimir/common/dto/extendedItemDto';
import type { GeneratedMediaUrlType, GeneratedMediaSignedUrlResponseDto, ScheduleHighresDeletionRequestDto } from '@mimir/common/dto/itemModelPrimitives';
import type {
  ItemCreationResponse,
  StartAnalyticsJobRequestDto
} from '@mimir/common/dto/itemModels';
import { READABLE_MDF_FIELDS_QUERY_PARAMETER } from '@mimir/common/dto/itemModelPrimitives';
import type { ItemMediaUsagesRequestDto } from '@mimir/common/dto/itemMediaUsageDto';
import type { DynamoItemType } from '@mimir/common/dto/itemTypes';
import type { ItemVersionDto as ItemVersion, ItemVersionUpdateDto, ItemVersionsHistoryDto, ItemVersionsHistoryDtoExt, LatestItemVersionDto } from '@mimir/common/dto/itemVersionDto';
import type { ItemAccessControl } from '@mimir/common/dto/itemAccessControlDto';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { MdfCreationRequest, MdfEntity } from '@mimir/common/dto/mdfModels';
import type { PersonImageDto, SimilarPersonsDto } from '@mimir/common/dto/personModel';
import type Relations from '@mimir/common/dto/relationModels';
import type { RelationType } from '@mimir/common/dto/relationsEntity';
import type { RelationTypeQueryParam, Relation, RelationsFromQueryParam, RelationsSizeQueryParam } from '@mimir/common/dto/relationModels';
import type { RestoreMediaRequestDto } from '@mimir/common/dto/restoreMediaRequestDto';
import type { SequenceDetails } from '@mimir/primitive/nle/sequence';
import type { StorageType } from '@mimir/common/dto/storageModel';
import type TenantModel from '@mimir/common/dto/tenantModel';
import type { ItemOfflineStorageInfo } from '@mimir/common/dto/offlineStorageInfoDto';
import type {
  Availability,
  TenantConfigDto,
  AnalyticConfig,
  ArchiveConfig,
  CreateTenantRequestDto,
  UpdateTenantConfigDto,
  TenantIdsDto,
  TenantSlimDto
} from '@mimir/common/dto/tenantModel';
import type { TimedMetadataCollection, TimedMetadataItem, UpdateTimedMetadataRequestDto } from '@mimir/common/dto/timedMetadataModels';
import type {
  DeleteTransferRequestDto,
  MarkTransferRequestDto,
  TransferRequestDto,
  TransferRequestPostRequestDtoV2,
  TransferRequestPutRequestDto
} from '@mimir/common/dto/transferRequestDtos';
import {
  TransferStateDto,
} from '@mimir/common/dto/transferRequestDtos';
import type {
  UpdateDefaultUploadLocationDto,
  UploadLocationClientDto,
  CreateUploadLocationDto
} from '@mimir/common/dto/uploadLocationDto';
import type UserDto from '@mimir/common/dto/userDto';
import type { TenantAdminPostRequestDto, UserModelView, CreateUserDto, UpdateUserDto, UserInfoDto, UserStatusDto } from '@mimir/common/dto/userDto';
import type { AttachmentUploadPreCheckDtO, CreateItemAttachmentRequestDto, DeleteItemAttachmentRequestDto, ItemAttachmentDtoWithUserId, UpdateItemAttachmentRequestDto, ItemAttachmentWithDownloadUrlDto } from '@mimir/common/dto/itemAttachmentDto';
import type { UserAvatarUploadDto, UserAvatarUploadUrlRequest, UserSettings } from '@mimir/common/dto/userSettingsModels';
import type { CreateVirtualClipPayload } from '@mimir/common/dto/virtualClipUtil';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import type { HttpQueryParameters, AdditionalParams, ApiCallResult } from '@mimir/common/util/apiClient';
import type { DisplayAspectRatio } from '@mimir/common/types/displayAspectRatio';
import { apiPut, apiDelete, apiPost, apiGet, apiPatch } from '@mimir/common/util/http';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import pick from 'lodash/pick';
import type { CreateChangelogDto, ChangelogDto, UpdateChangelogDto } from '@mimir/common/dto/changelogDto';
import { ofAaf, ofAllRouterControllerStatuses, ofAllRouterSources, ofAttachments, ofAvatar, ofBatchConcludeJobUploadURLs, ofBatchItems, ofBatchSignUploadURLs, ofBookmark, ofBookmarkFolder, ofBookmarks, ofCatchUpStorageIngest, ofChangelogs, ofClipList, ofColdStorageConfigStatus, ofConfig, ofCustomAction, ofCustomActions, ofCustomIcon, ofCustomIcons, ofDeduplication, ofDynamicSourceList, ofExternalShare, ofExternalShares, ofFeatureFlag, ofFeatureFlags, ofGeneratedMediaLocation, ofGeneratedMediaLocations, ofGeneratedMediaUploadUrl, ofGetIPAddress, ofGroup, ofGroups, ofHousekeepingRun, ofHousekeepingState, ofImage, ofImages, ofIngestFile, ofIngestStorageConfig, ofIngestStorageConfigs, ofItem, ofItemActivity, ofItemAnalysis, ofItemAnalytics, ofItemArchive, ofItemComment, ofItemCommentEdit, ofItemCommentReactions, ofItemCommentStatus, ofItemComments, ofItemCustomIcon, ofItemEditStatus, ofItemHighresDeletionSchedule, ofItemMediaUsage, ofItemMetadata, ofItemRender, ofItemTaskCancel, ofItemUndelete, ofItemWarnings, ofItems, ofItemsByExternalId, ofKeldaMediaPipelineStatus, ofLabels, ofLifecycleConfig, ofLiveItem, ofLiveItems, ofLiveLocation, ofLiveLocations, ofLivePipelines, ofLiveSourceV2, ofLiveSourcesV2, ofMarkTransferRequest, ofMdf, ofMdfs, ofMediaPipelineRouter, ofMediaSegments, ofMergeItems, ofMetering, ofMos, ofMosAdminMessages, ofNdiRouter, ofNdiSources, ofNextMosMessage, ofOptionList, ofOptionLists, ofPackages, ofPagedKeldaRequests, ofPendingOperationsSummary, ofPerson, ofPersons, ofPlayoutCommands, ofPlayoutDevices, ofPlayoutStatus, ofProcess, ofQc, ofRecordLog, ofRecordingSeries, ofRecordingSeriesCollection, ofRecordingSeriesItems, ofRelation, ofRelationType, ofRelations, ofRenderSequenceRequests, ofRestoreMedia, ofRouter, ofRouterCrosspoints, ofRouterMessages, ofRouters, ofScheduledRecording, ofScheduledRecordings, ofSearch, ofSearchItemAnalytics, ofSettings, ofSsoConfig, ofStats, ofStorageManagers, ofStorageTransfers, ofStorages, ofSystemConfigTranscriptionLanguageSettings, ofTenant, ofTenantAdmins, ofTenantConfigColdStorage, ofTenantConfigDefaultExportLocation, ofTenantConfigDefaultUploadLocation, ofTenantConfigDisplayAspectRatio, ofTenantConfigTranscriptionLanguageSettings, ofTenantIds, ofTenantSettings, ofTenantStorage, ofTenantStorages, ofTenantSystemActivity, ofTenants, ofThumbnailUrlInTime, ofTimedMetadata, ofTimeline, ofTranscript, ofTransferRequest, ofTransferRequests, ofTransfers, ofUpdateFolderMetadata, ofUser, ofUserInfo, ofUserSettings, ofUserSignOut, ofUserStatus, ofUserTask, ofUserTasks, ofUsers, ofVerifyExternalStorage, ofVersionItem, ofVersionsHistory, ofVirtualClips, ofWebhook, ofWebhooks, ofTenantLogo, ofShareInvitations, ofRouterSources, ofItemExternalHighresInfoAppendRetrievedSegment, ofItemExternalHighresInfo, ofTimedMetadataById, ofOfflineStorageJobs, ofOfflineStorageJob, ofItemOfflineStorageInfo} from '@mimir/common/aws/api/path';
import { getEmbed } from '@mimir/common/client/hal';
import { getPath } from '@mimir/common/util/getPath';
import type {
  ClientExternalLocationDto,
  UpdateExternalLocationDto,
  ColdStorageApiCommonDto,
  SetLifecycleConfigResponseDto,
  BucketColdStorageConfigStatusDto,
} from '@mimir/common/dto/bucketConfigDto';
import type LiveLocationDto from '@mimir/common/dto/LiveLocationDto';
import type { CreateOrUpdateLiveLocationDto } from '@mimir/common/dto/LiveLocationDto';
import type { ExternalStorageVerificationRequest, ExternalStorageVerificationResponse } from '@mimir/common/dto/externalStorageVerificationDto';
import type { MinioConfigDto, UpdateMinioConfigDto, CreateMinioConfigDto, PackageDefinition } from '@mimir/common/dto/keldaConfigPrimitives';
import type {
  CountStatisticsResponseDto,
  GetCountStatisticsQueryParams,
  LogStaticRequestDto
} from '@mimir/common/dto/statsDtos';
import type { CatchupIngestStorageRequest } from '@mimir/common/dto/catchUpIngestStorageDto';
import type { AafDto, AafGenerationRequest } from '@mimir/common/dto/aafDto';
import type {
  MosRequestFromNcsDto,
  MosResponseFromBackendDto,
  MosResponseFromNcsDto,
  MosGetNextMessageResponseDto,
  MosAdminGetMessagesResponseDto,
  MosAdminOperationRequestDto,
  MosAdminGetMessagesType,
  MosLockReleaseRequestDto,
  MosMessageQueue
} from '@mimir/common/dto/mosDtos';
import type { MergeItemsRequest } from '@mimir/common/dto/mergeItemsDto';
import type { ExpectedPipelineDto, GetSelecteDynamicSourceDto, LiveSourceDtoV2, LiveSourceStatus, SourceListKey, UpdateLiveSourceDtoV2, UpdateSelectedDynamicSourceDto } from '@mimir/common/dto/liveSourceDtos';
import type { GeneratedMediaLocationDto, UpdateGeneratedMediaLocationDto, CreateGeneratedMediaLocationDto } from '@mimir/common/dto/generatedMediaLocationDtos';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { MediaSegmentsResponse } from '@mimir/common/dto/mediaDtos';
import type { CrosspointConnectionRequest, RouterControllerStatusDto, RouterDto, RouterMessageDto, RouterSourceDto, UpdateRouterDto } from '@mimir/common/dto/routerControllerDtos';
import type { OptionListDto, OptionListIdsDto, UpdateOptionListDto } from '@mimir/common/dto/optionListDto';
import type { DeduplicationRequestDto, DeduplicationResultDto } from '@mimir/common/dto/deduplicationDtos';
import type { ItemEditStatusCreateDto, ItemEditStatusDeleteDto, ItemEditStatusDto } from '@mimir/common/dto/itemEditStatusDto';
import type { KeldaPlayoutCommandDto, PlayoutDeviceDto, PlayoutDeviceStatusGetDto, ResetPlayoutDto } from '@mimir/common/dto/playoutDtos';
import type { ExternalShareGetDto, ShareCreationRequestDto, ShareInvitationRequestDto, ShareUpdateRequestDto } from '@mimir/common/dto/shareDtos';
import { ALL_FOR_TENANT_PARAM, CONTENT_ID_PARAM } from '@mimir/common/dto/shareDtos';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { StorageLocationApiResponse, StorageLocationCreationDto, StorageLocationDeleteDto, StorageLocationUpdateDto } from '@mimir/common/dto/storageLocationDto';
import type { StorageTransferDto, StorageTransferCreationDto, GetStorageTransferQueryParams } from '@mimir/common/dto/storageTransferDto';
import type { NdiSourcesForTenantDto, UpdateNdiCrosspointDto } from '@mimir/common/dto/ndiDtos';
import type { IngestFileDto } from '@mimir/common/dto/ingestFileDtos';
import type { BackendClient } from '@mimir/common/util/backendClient';
import type { HousekeepingRunResponse, HousekeepingTaskStateResponse } from '@mimir/common/dto/housekeepingApiDto';
import { assumeResultString, assumeResultObject, assumeResultArray, assumeResultObjectOrNull, vagueResult, assumeEmbedCollection, assumeResultObjectOrNoContent } from '@mimir/common/client/assumeResult';
import { ListUsersCommand } from '@mimir/common/api/userCommands';
import { CreateLocationCommand, DeleteLocationCommand, ListUploadLocationsCommand } from '@mimir/common/api/locationCommands';
import type { RenderSequenceDto, RenderSequencePostResponseDto } from '@mimir/common/dto/renderSequence';
import type { ItemRenderDto } from '@mimir/common/dto/itemRenderDto';
import type { CreateSecureApiKeyResponseDto, SecureApiKeyDto, SecureApiKeyModifyRequestDto } from '@mimir/common/dto/secureApiKeyDto';
import type { IPAddressDto } from '@mimir/common/dto/ipAddressDto';
import type { MigrateMediaRequestDto } from '@mimir/common/dto/migrateMediaDtos';
import type { EditCommentDto, GetCommentDto, GetCommentsResponseDto, PostCommentRequestDto, ReactionsUpdateDto, UpdateCommentStatusDto } from '@mimir/common/dto/commentDtos';
import type { GetUserTaskResponseDto, GetUserTasksQueryParams, GetUserTasksResponseDto } from '@mimir/common/dto/userTasksDtos';
import type { CreateCustomActionItemDto, CustomActionItemDto, UpdateCustomActionItemDto } from '@mimir/common/dto/customActionDtos';
import { callApiCommand } from '../api/callApiCommand';
import { injectDefaultQueryParamsForItemApis } from '../api/injectItemQueryParams';
import type { FolderMetadataDto, FolderMetadataUpdatePayload } from '@mimir/common/dto/folderModels';
import type { TaskInfo, CancelTaskResponseDto } from '@mimir/common/dto/taskInfo';
import type { CreateWebhookDto, UpdateWebhookDto, WebhookDto } from '@mimir/common/dto/webhookDtos';
import type { BatchCreateItemsRequestDto, BatchCreateItemsResponseDto } from '@mimir/common/dto/batchCreateItems';
import type { BatchUpdateItemsRequestDto, BatchUpdateItemsResponseDto } from '@mimir/common/dto/batchUpdateItems';
import type { BatchGetItemsByExternalIdResponseDto, BatchGetItemsByExternalIdRequestDto } from '@mimir/common/dto/batchGetItemsByExternalId';
import type { ItemMetadataUpdateRequestDto, ItemMetadataUpdateResponseDto } from '@mimir/common/dto/itemMetadataUpdate';
import type { BatchDeleteItemsRequestDto, BatchDeleteItemsResponseDto } from '@mimir/common/dto/batchDeleteItems';
import type { CreateCustomIconDto, CustomIconDto, CustomIconUploadDto } from '@mimir/common/dto/customIconDtos';
import type { ItemWarningsDto } from '@mimir/common/dto/itemWarningsDto';
import type { GetKeldaMediaPipelineStatusDto, UpdateKeldaMediaPipelineStatusDto } from '@mimir/common/dto/keldaMediaPipelineStatusDto';
import type { BatchConcludeJobUploadInput, BatchConcludeJobUploadResponse, BatchSignJobUploadUrlsInput, BatchSignJobUploadUrlsOutput } from '@mimir/common/dto/jobUploadDtos';
import type { TimelineDto } from '@mimir/common/dto/cutter/timelineDto';
import type { CreatePluginDto, PluginDto, UpdatePluginDto } from '@mimir/common/dto/pluginDto';
import type { PagedKeldaRequestsDto } from '@mimir/common/dto/keldaRequestDtos';
import { assumeObject } from '@mimir/common/type-guards/assumeType';
import type { ExternalHighresInfoDto, RetrievedSegmentRangeDto } from '@mimir/common/dto/externalHighresInfoDto';
import type { GetOfflineStoragePendingJobsDto, GetOfflineStorageStartedJobsDto, OfflineStorageJobUpdate } from '@mimir/common/dto/offlineStorageJobDto';
import type { OfflineStoragePluginCreateDto, OfflineStoragePluginUpdateDto, OfflineStoragePluginDto, OfflineStoragePluginsDto } from '@mimir/common/dto/offlineStoragePluginDto';
import type { ApiKeysAdminDto, UpdateApiKeyPayload } from '@mimir/common/dto/apiKeysAdminDto';
/*****************************************************************************
 * The api_XXX functions below do not depend on any UI/browser code.         *
 * They are safe to use from the integration tests and Storage Manager.      *
 *                                                                           *
 * If you need to call a function from the UI code, you should use           *
 * the functions in clients/vuetify/src/util/api.ts instead as those         *
 * already wrap them for you with the correct ApiClient and ServiceLinks  *
 * parameters.                                                               *
 *****************************************************************************/

/************************ items ************************/

export const api_getItem = (c: BackendClient, itemId: string, additionalParams: AdditionalParams = {}): Promise<LoadedItem> =>
  apiGet(c.getApiClient(), ofItem(c.getServiceLinks(), itemId), injectDefaultQueryParamsForItemApis(additionalParams)).then(result => assumeResultObject<LoadedItem>(result, 'id'));

export const api_deleteItem = (c: BackendClient, itemId: string, immediate: boolean): Promise<ApiCallResult> =>
  apiDelete(c.getApiClient(), ofItem(c.getServiceLinks(), itemId), {}, { queryParams: { immediate }});

export const api_getItemFields = <Key extends keyof LoadedItem>(c: BackendClient, itemId: string, includeFields: Readonly<Key[]>, readableMetadataFields = false): Promise<Pick<LoadedItem, Key>> =>
  apiGet(
    c.getApiClient(),
    ofItem(c.getServiceLinks(), itemId),
    injectDefaultQueryParamsForItemApis({ queryParams: { fields: includeFields.join(','), [READABLE_MDF_FIELDS_QUERY_PARAMETER]: readableMetadataFields }})
  )
    .then(result => assumeResultObject<Pick<LoadedItem, Key>>(result));

export const api_getThumbnailUrlInTime = (c: BackendClient, itemId: string, inPoint: string, additionalParams?: AdditionalParams): Promise<string> => {
  return apiGet(c.getApiClient(), ofThumbnailUrlInTime(c.getServiceLinks(), itemId, inPoint), additionalParams).then(result => assumeResultString(result));
};

export const api_getClipList = (c: BackendClient, itemId: string): Promise<ClipListDto> =>
  apiGet(c.getApiClient(), ofClipList(c.getServiceLinks(), itemId)).then(result => assumeResultObject<ClipListDto>(result));

export const api_updateClipList = (c: BackendClient, itemId: string, clipList: ClipListUpdateDto, version: number) => {
  return apiPut(c.getApiClient(), ofClipList(c.getServiceLinks(), itemId), clipList, { headers: { 'If-Match': version }});
};

export const api_getTimeline = (c: BackendClient, itemId: string): Promise<TimelineDto> =>
  apiGet(c.getApiClient(), ofTimeline(c.getServiceLinks(), itemId)).then(result => assumeResultObject<TimelineDto>(result));

export const api_updateTimeline = (c: BackendClient, itemId: string, payload: TimelineDto) => {
  return apiPut(c.getApiClient(), ofTimeline(c.getServiceLinks(), itemId), payload);
};

/**
 * Removes the mark-for-deletion flag on the given item.
 */
export const api_undeleteItem = (c: BackendClient, itemId: string): Promise<any> =>
  apiPost(c.getApiClient(), ofItemUndelete(c.getServiceLinks(), itemId))
    .then(result => vagueResult(result));

export const api_reprocessItem = (c: BackendClient, itemId: string, reprocessActionType: ReprocessActionType, supportTenantId?: string): Promise<Partial<LoadedItem>> => {
  const body  = supportTenantId ? { reprocessActionType, supportTenantId } : { reprocessActionType };
  return apiPost(c.getApiClient(), ofItem(c.getServiceLinks(), itemId), body)
  .then(result => assumeResultObject<Partial<LoadedItem>>(result));
};

export const api_createPlaceholder = async (c: BackendClient, props: Partial<ItemCreationRequestDto>): Promise<ItemCreationResponse> => {
  const body = {
    ...props,
  };
  const response = await apiPost(c.getApiClient(), ofItems(c.getServiceLinks()), body, injectDefaultQueryParamsForItemApis({}));
  return assumeResultObject<ItemCreationResponse>(response, 'id');
};

/** Creates a new item entry. */
export interface ApiCreateItemParams {
  /** Title to use for the item. By default, it will be the file name. */
  title: string;
  /** Name of the media file that will be uploaded. */
  filename: string;
  fileType: string;
  /** Config for the analytic operations to be run on the item. */
  analyticConfig: AnalyticConfig;
  /** An array of folder IDs or null. The newly created item will appear in all the given folders. */
  folderParents: string[] | null;
  /**
   * When an item is created with segments from existing Mimir items (like in Adobe Premiere),
   * this array is used to track the origin of the content by listing the IDs of the items that
   * were used to create this new item.
   */
  parentItemIds: string[] | undefined;
  /**
   * When an item is created with segments from existing Mimir items (like in Adobe Premiere),
   * this array is used to describe these segments.
   */
  sequenceDetails: SequenceDetails | undefined;
  metadata: ItemMetaData | undefined;
  isHidden: boolean | undefined;
  isP2Part: boolean | undefined;
  itemType?: DynamoItemType;
  progressInfo?: ProgressInfo;
  options?: AdditionalParams;
  relatedItemIds?: string[];
  upstreamItemId?: string;
  visibleTo?: ItemAccessControl;
  updatableBy?: ItemAccessControl;
}

export const api_createItem = async (
  c: BackendClient,
  params: ApiCreateItemParams,
): Promise<ItemCreationResponse> => {
  const body: ItemCreationRequestDto = {
    title: params.title,
    filename: params.filename,
    fileType: params.fileType,
    folderParents: params.folderParents,
    transcriptionService: params.analyticConfig.transcriptionConfig?.transcriptionService,
    transcriptionChannels: params.analyticConfig.transcriptionConfig?.transcriptionChannels,
    transcriptionChannelIdentification: params.analyticConfig.transcriptionConfig?.transcriptionChannelIdentification,
    parentItemIds: params.parentItemIds,
    sequenceDetails: params.sequenceDetails,
    languageCode: params.analyticConfig.transcriptionConfig?.transcriptionSourceLanguage,
    transcriptionEnabled: params.analyticConfig.transcriptionEnabled,
    labelDetectionEnabled: params.analyticConfig.labelDetectionEnabled,
    textDetectionEnabled: params.analyticConfig.textDetectionEnabled,
    personDetectionEnabled: params.analyticConfig.personDetectionEnabled,
    createUnknownPersons: params.analyticConfig.personDetectionConfig?.createUnknownPersons,
    celebrityDetectionEnabled: params.analyticConfig.celebrityDetectionEnabled,
    textDetectionSettings: params.analyticConfig.textDetectionSettings,
    analyticPlugins: params.analyticConfig.analyticPlugins,
    metadata: params.metadata,
    isHidden: params.isHidden,
    isP2Part: params.isP2Part,
    ...(params.itemType && { itemType: params.itemType }),
    ...(params.progressInfo && { progressInfo: params.progressInfo }),
    ...(params.relatedItemIds && { relatedItemIds: params.relatedItemIds }),
    ...(params.upstreamItemId && { upstreamItemId: params.upstreamItemId }),
    visibleTo: params.visibleTo,
    updatableBy: params.updatableBy,
  };

  const response = await apiPost(c.getApiClient(), ofItems(c.getServiceLinks()), body, injectDefaultQueryParamsForItemApis(params.options));
  return assumeResultObject<ItemCreationResponse>(response);
};

export async function api_batchCreateItems(
  c: BackendClient,
  dto: BatchCreateItemsRequestDto,
  options?: AdditionalParams,
) {
  const response = await apiPost(c.getApiClient(), ofBatchItems(c.getServiceLinks()), dto, injectDefaultQueryParamsForItemApis(options));
  return assumeResultObject<BatchCreateItemsResponseDto>(response, 'items');
}

export async function api_batchUpdateItems(
  c: BackendClient,
  dto: BatchUpdateItemsRequestDto,
  options?: AdditionalParams,
) {
  const response = await apiPatch(c.getApiClient(), ofBatchItems(c.getServiceLinks()), dto, injectDefaultQueryParamsForItemApis(options));
  return assumeResultObject<BatchUpdateItemsResponseDto>(response, 'updates');
}

export async function api_itemMetadataUpdate(
  c: BackendClient,
  itemId: string,
  dto: ItemMetadataUpdateRequestDto,
  options?: AdditionalParams,
) {
  const response = await apiPatch(c.getApiClient(), ofItemMetadata(c.getServiceLinks(), itemId), dto, injectDefaultQueryParamsForItemApis(options));
  return assumeResultObject<ItemMetadataUpdateResponseDto>(response, 'metadata');
}

/**
 * Warning: Api gateway has a quota of 10240 bytes for "Total combined size of request line and header values".
 * So it is assumed that the caller of this api will chunk the external ids as the ids are part of query string i.e., HTTP request line.
 */
export async function api_batchGetItemsByExternalId(
  c: BackendClient,
  dto: BatchGetItemsByExternalIdRequestDto,
) {
  const queryParams = { ids: JSON.stringify(dto.externalIds) };
  const response = await apiGet(c.getApiClient(), ofItemsByExternalId(c.getServiceLinks()), { queryParams });
  return assumeResultObject<BatchGetItemsByExternalIdResponseDto>(response, 'items');
}

export async function api_batchDeleteItems(
  c: BackendClient,
  dto: BatchDeleteItemsRequestDto,
  options?: AdditionalParams,
) {
  const response = await apiDelete(c.getApiClient(), ofBatchItems(c.getServiceLinks()), dto, injectDefaultQueryParamsForItemApis(options));
  return assumeResultObject<BatchDeleteItemsResponseDto>(response, 'deletes');
}

/**
 * Updates an item properties by its id
 * @param payload an object that contains the properties which needs to be updated
 *
 * TODO: Define a proper UpdateItemResponseDto
 */
export const api_updateItem = (c: BackendClient, itemId: string, payload: ItemUpdateRequestDto, options?: AdditionalParams): Promise<Partial<ExtendedItemDto>> =>
  apiPost(c.getApiClient(), ofItem(c.getServiceLinks(), itemId), payload, injectDefaultQueryParamsForItemApis(options)).then(result => assumeResultObject<Partial<ExtendedItemDto>>(result));

export const api_updateItemCustomIcon = (c: BackendClient, itemId: string, payload: ItemCustomIconUpdateDto, options?: AdditionalParams): Promise<string | void> =>
  apiPatch(c.getApiClient(), ofItemCustomIcon(c.getServiceLinks(), itemId), payload, injectDefaultQueryParamsForItemApis(options)).then(result => assumeResultString(result));

export const api_updateItemMediaUsage = async (c: BackendClient, itemId: string, payload: ItemMediaUsagesRequestDto): Promise<any> =>
  apiPost(c.getApiClient(), ofItemMediaUsage(c.getServiceLinks(), itemId), payload);

export const api_scheduleHighresDeletion = async (c: BackendClient, itemId: string, payload: ScheduleHighresDeletionRequestDto): Promise<any> =>
  apiPost(c.getApiClient(), ofItemHighresDeletionSchedule(c.getServiceLinks(), itemId), payload).then(result => vagueResult(result));

export const api_unscheduleHighresDeletion = async (c: BackendClient, itemId: string): Promise<any> =>
  apiDelete(c.getApiClient(), ofItemHighresDeletionSchedule(c.getServiceLinks(), itemId));

export const api_cancelTask = async (c: BackendClient, itemId: string, taskInfo: TaskInfo): Promise<CancelTaskResponseDto> =>
  apiPost(c.getApiClient(), ofItemTaskCancel(c.getServiceLinks(), itemId), taskInfo).then(result => assumeResultObject<CancelTaskResponseDto>(result));

/************************ operations on items ************************/


export const api_logWarningOnItem = (c: BackendClient, itemId: string, payload: ItemWarningsDto): Promise<void> =>
  apiPut(c.getApiClient(), ofItemWarnings(c.getServiceLinks(), itemId), payload).then((_) => { });

export const api_createVirtualClip = async (c: BackendClient, payload: VirtualClipModel | CreateVirtualClipPayload): Promise<{ id: string }> =>
  apiPost(c.getApiClient(), ofVirtualClips(c.getServiceLinks()), payload).then(result => assumeResultObject<{ id: string }>(result, 'id'));


export const api_saveTranscript = async (c: BackendClient, itemId: string, payload: any) => {
  return apiPut(c.getApiClient(), ofTranscript(c.getServiceLinks(), itemId), payload);
};


export const api_deleteLabels = async (c: BackendClient, itemId: string, labelsToRemove: string[]) =>
  apiDelete(c.getApiClient(), ofLabels(c.getServiceLinks(), itemId), { labels: labelsToRemove }, {});


export const api_startBackendJob = async (c: BackendClient, payload: StartAnalyticsJobRequestDto) =>
  apiPost(c.getApiClient(), ofProcess(c.getServiceLinks()), payload);


export const api_search = async (c: BackendClient, additionalParams: AdditionalParams) =>
  apiGet(c.getApiClient(), ofSearch(c.getServiceLinks()), injectDefaultQueryParamsForItemApis(additionalParams)).then(result => vagueResult(result));


export const api_getComments = async (c: BackendClient, itemId: string): Promise<GetCommentsResponseDto> =>
  apiGet(c.getApiClient(), ofItemComments(c.getServiceLinks(), itemId)).then(result => assumeResultObject<GetCommentsResponseDto>(result, 'comments'));

export const api_getComment = async (c: BackendClient, itemId: string, commentId: string): Promise<GetCommentDto> =>
  apiGet(c.getApiClient(), ofItemComment(c.getServiceLinks(), itemId, commentId)).then(result => assumeResultObject<GetCommentDto>(result, 'text'));

export const api_postComment = async (c: BackendClient, itemId: string, comment: PostCommentRequestDto): Promise<GetCommentDto> =>
  apiPost(c.getApiClient(), ofItemComments(c.getServiceLinks(), itemId), comment).then(result => assumeResultObject<GetCommentDto>(result, 'text'));

export const api_updateCommentStatus = async (c: BackendClient, itemId: string, commentId: string, update: UpdateCommentStatusDto): Promise<GetCommentDto | null> =>
  apiPut(c.getApiClient(), ofItemCommentStatus(c.getServiceLinks(), itemId, commentId), update).then(result => assumeResultObjectOrNoContent<GetCommentDto>(result, 'text'));

export const api_updateCommentText = async (c: BackendClient, itemId: string, commentId: string, update: EditCommentDto): Promise<GetCommentDto | null> =>
  apiPut(c.getApiClient(), ofItemCommentEdit(c.getServiceLinks(), itemId, commentId), update).then(result => assumeResultObjectOrNoContent<GetCommentDto>(result, 'text'));

export const api_updateCommentReaction = async (c: BackendClient, itemId: string, commentId: string, update: ReactionsUpdateDto): Promise<GetCommentDto | null> =>
  apiPost(c.getApiClient(), ofItemCommentReactions(c.getServiceLinks(), itemId, commentId), update).then(result => assumeResultObjectOrNoContent<GetCommentDto>(result, 'text'));

export const api_deleteComment = async (c: BackendClient, itemId: string, commentId: string): Promise<GetCommentDto | null> =>
  apiDelete(c.getApiClient(), ofItemComment(c.getServiceLinks(), itemId, commentId))
    .then(result => result.status === 404 ? null : assumeResultObjectOrNoContent<GetCommentDto>(result, 'text'));

export const api_getLatestItemVersion = async (c: BackendClient, itemId: string): Promise<LatestItemVersionDto> =>
  apiGet(c.getApiClient(), ofVersionItem(c.getServiceLinks(), itemId)).then(result => assumeResultObject<LatestItemVersionDto>(result, 'id'));


export const api_createItemReplacement = async (c: BackendClient, itemId: string, payload: ItemVersion, queryParams: AdditionalParams = {}): Promise<LoadedItem> =>
  apiPost(c.getApiClient(), ofVersionItem(c.getServiceLinks(), itemId), payload, injectDefaultQueryParamsForItemApis(queryParams)).then(result => assumeResultObject<LoadedItem>(result, 'id'));


export function api_getItemVersionsHistory(c: BackendClient, itemId: string): Promise<ItemVersionsHistoryDto>;
export function api_getItemVersionsHistory(c: BackendClient, itemId: string, fetchItemProperties: true): Promise<ItemVersionsHistoryDtoExt>;
export async function api_getItemVersionsHistory(c: BackendClient, itemId: string, fetchItemProperties?: true): Promise<ItemVersionsHistoryDto | ItemVersionsHistoryDtoExt> {
  return apiGet(c.getApiClient(), ofVersionsHistory(c.getServiceLinks(), itemId), fetchItemProperties ? injectDefaultQueryParamsForItemApis({ queryParams: { fetchItemProperties } }) : {})
    .then(result => assumeResultObject<ItemVersionsHistoryDto | ItemVersionsHistoryDtoExt>(result, 'versions'));
}

export async function api_updateItemVersion(c: BackendClient, itemId: string, payload: ItemVersionUpdateDto): Promise<void> {
  await apiPut(c.getApiClient(), ofVersionsHistory(c.getServiceLinks(), itemId), payload);
}

export const api_promoteOldItemVersionToLatestVersion = async (c: BackendClient, itemId: string): Promise<LoadedItem> =>
  apiPost(c.getApiClient(), ofVersionsHistory(c.getServiceLinks(), itemId)).then(result => assumeResultObject<LoadedItem>(result, 'id'));


export interface GetItemRelationsParams {
  [RelationTypeQueryParam]?: RelationType; // default all
  [RelationsSizeQueryParam]?: number;
  [RelationsFromQueryParam]?: number;
}

export const api_getItemRelations = async (
  c: BackendClient,
  itemId: string,
  queryParams?: GetItemRelationsParams,
): Promise<Relations> => {
  return apiGet(
    c.getApiClient(),
    ofRelations(c.getServiceLinks(), itemId),
    injectDefaultQueryParamsForItemApis({ queryParams }),
  ).then(result => assumeResultArray<Relation>(result));
};

export const api_updateItemRelations = async (c: BackendClient, itemId: string, relationType: RelationType, relatedItemIds: string[], options?: AdditionalParams): Promise<void> =>
  apiPut(c.getApiClient(), ofRelationType(c.getServiceLinks(), itemId, relationType), relatedItemIds, injectDefaultQueryParamsForItemApis(options)).then(() => {});


export const api_createItemRelation = async (c: BackendClient, itemId: string, relationType: RelationType, relatedItemId: string, executeSameActionOnCleanCopy?: boolean): Promise<void> =>
  apiPost(
    c.getApiClient(),
    ofRelationType(c.getServiceLinks(), itemId, relationType),
    { relatedItemId, ...(executeSameActionOnCleanCopy && { executeSameActionOnArchiveCopy:  true}) },
    injectDefaultQueryParamsForItemApis({})
  ).then(() => {});


export const api_removeItemRelation = async (c: BackendClient, itemId: string, relationType: RelationType, relatedItemId: string): Promise<void> =>
  apiDelete(c.getApiClient(), ofRelation(c.getServiceLinks(), itemId, relationType, relatedItemId)).then(() => {});


export const api_updateArchiveStatus = async (c: BackendClient, itemId: string,
  payload: ArchiveOperationRequestDto, additionalParams: AdditionalParams = {}): Promise<ArchiveOperationResponseDto> =>
  apiPut(c.getApiClient(), ofItemArchive(c.getServiceLinks(), itemId), payload, additionalParams).then(result => assumeResultObject<ArchiveOperationResponseDto>(result, 'type'));


export const api_createAttachmentToItem = async (c: BackendClient, itemId: string, payload: CreateItemAttachmentRequestDto): Promise<string> =>
  apiPost(c.getApiClient(), ofAttachments(c.getServiceLinks(), itemId), payload).then(result => assumeResultString(result));


export const api_attachmentUploadPrecheck = async (c: BackendClient, itemId: string, payload: CreateItemAttachmentRequestDto): Promise<AttachmentUploadPreCheckDtO> =>
  apiPost(c.getApiClient(), ofAttachments(c.getServiceLinks(), itemId), payload, { queryParams: { isPrecheck : true } })
  .then(result => {
    return result.data as AttachmentUploadPreCheckDtO;
  });


export const api_getAttachmentsToItem = (c: BackendClient, itemId: string, shouldExpirePosterFrameUrlEarly?: boolean): Promise<ItemAttachmentWithDownloadUrlDto[]> =>
  apiGet(c.getApiClient(), ofAttachments(c.getServiceLinks(), itemId), { queryParams: { shouldExpirePosterFrameUrlEarly }}).then(result => assumeResultArray<ItemAttachmentWithDownloadUrlDto>(result));


export const api_getAttachmentToItemByName = (c: BackendClient, itemId: string, attachmentFileName: string, shouldExpirePosterFrameUrlEarly?: boolean): Promise<string> =>
  apiGet(c.getApiClient(), ofAttachments(c.getServiceLinks(), itemId), { queryParams: { attachment: attachmentFileName, shouldExpirePosterFrameUrlEarly }}).then(result => assumeResultString(result));


export const api_removeAttachmentFromItem = async (c: BackendClient, itemId: string, attachment: DeleteItemAttachmentRequestDto) =>
  apiDelete(c.getApiClient(), ofAttachments(c.getServiceLinks(), itemId), attachment, {});


export const api_updateItemAttachment = async (c: BackendClient, itemId: string, payload: UpdateItemAttachmentRequestDto): Promise<ItemAttachmentDtoWithUserId> =>
  apiPut(c.getApiClient(), ofAttachments(c.getServiceLinks(), itemId), payload).then(result => assumeResultObject<ItemAttachmentDtoWithUserId>(result, 'userId'));


export const api_createGeneratedMediaUploadUrl = async (
  c: BackendClient, itemId: string, type: GeneratedMediaUrlType
): Promise<GeneratedMediaSignedUrlResponseDto> => {
  const params: AdditionalParams = { queryParams: { type } };
  return apiPost(c.getApiClient(), ofGeneratedMediaUploadUrl(c.getServiceLinks(), itemId), undefined, params)
    .then(result => {
      // TODO: Investigate strange wrapping
      const wrapped = assumeResultObject<{ data: GeneratedMediaSignedUrlResponseDto }>(result, 'data');
      return wrapped.data;
    });
};

export const api_batchSignTaskUploadURLs = async (
  c: BackendClient,
  dto: BatchSignJobUploadUrlsInput,
  keldaRequestId: string,
  highresUploadLocationId?: string,
): Promise<BatchSignJobUploadUrlsOutput> => {
  return apiPost(c.getApiClient(), ofBatchSignUploadURLs(c.getServiceLinks()), dto, {queryParams: {requestId: keldaRequestId, highresUploadLocationId}})
    .then(result => assumeResultObject<BatchSignJobUploadUrlsOutput>(result, 'batch'));
};

export const api_batchConcludeJobUploadURLs = async (
  c: BackendClient,
  dto: BatchConcludeJobUploadInput,
  keldaRequestId: string,
  highresUploadLocationId?: string,
  passive?: boolean
): Promise<BatchConcludeJobUploadResponse> => {
  return apiPost(c.getApiClient(), ofBatchConcludeJobUploadURLs(c.getServiceLinks()), dto, {queryParams: {requestId: keldaRequestId, highresUploadLocationId, passive}})
    .then(result => assumeResultObject<BatchConcludeJobUploadResponse>(result, 'batch'));
};

export const api_getPagedKeldaRequests = async (c: BackendClient,  nextPageStartKey: string | undefined, supportTenantId?: string): Promise<PagedKeldaRequestsDto> => {
  const queryParams: HttpQueryParameters = {};
  if (supportTenantId) {
    queryParams.supportTenantId = supportTenantId;
  }
  if (nextPageStartKey) {
    queryParams.nextPageStartKey = nextPageStartKey;
  }
  return apiGet(c.getApiClient(), ofPagedKeldaRequests(c.getServiceLinks()), {queryParams}).then((result) => assumeResultObject<PagedKeldaRequestsDto>(result));
};


export const api_generateAaf = async (c: BackendClient, itemId: string, payload: AafGenerationRequest): Promise<AafDto> =>
  apiPost(c.getApiClient(), ofAaf(c.getServiceLinks(), itemId), payload).then(result => assumeResultObject<AafDto>(result, 'base64Aaf'));


export const api_mergeItems = async (c: BackendClient, payload: MergeItemsRequest): Promise<void> =>
  apiPost(c.getApiClient(), ofMergeItems(c.getServiceLinks()), payload).then(() => {});


/**
 * Returns information about the current analytics operations for the given item.
 */
export const api_getItemAnalysisStatus = async (c: BackendClient, itemId: string): Promise<AnalyticsResult[]> =>
  apiGet(c.getApiClient(), ofItemAnalysis(c.getServiceLinks(), itemId)).then(result => assumeResultArray<AnalyticsResult>(result));


/**
 * Returns a list describing the history of the analytics operations
 * that have been performed for the given item.
 */
export const api_getItemAnalyticsHistory = async (c: BackendClient, id: string): Promise<ItemAnalyticsDto[]> =>
  apiGet(c.getApiClient(), ofItemAnalytics(c.getServiceLinks(), id)).then(result => assumeEmbedCollection<ItemAnalyticsDto>(result));


export const api_getItemSystemActivity = async (
  c: BackendClient,
  itemId: string,
  query: ItemActivityQueryParams,
): Promise<SystemActivityResponseDto> => {
  const queryParams = removeUndefinedKeys(query);
  return apiGet(c.getApiClient(), ofItemActivity(c.getServiceLinks(), itemId), { queryParams })
    .then(result => assumeResultObject<ItemActivityResponseDto>(result));
};

export const api_getTenantSystemActivity = async (
  c: BackendClient,
  query: TenantSystemActivityQueryParams,
): Promise<SystemActivityResponseDto> => {
  const queryParams = removeUndefinedKeys(query);
  return apiGet(c.getApiClient(), ofTenantSystemActivity(c.getServiceLinks()), { queryParams })
    .then(result => assumeResultObject<SystemActivityResponseDto>(result));
};

export const api_getSearchItemAnalyticsResult = async (
  c: BackendClient,
  query: AnalyticsSearchQuery
): Promise<AnalyticsSearchResults> => {
  const queryParams: HttpQueryParameters = {};
  if (query.itemsPerPage) {
    queryParams.itemsPerPage = query.itemsPerPage;
  }
  queryParams.from = query.from && query.from > 0 ? query.from : 0;
  queryParams.all = query.all;
  queryParams.sortOrder = query.sortDesc ? 'desc' : 'asc';
  if (query.searchString) {
    queryParams.searchString = query.searchString;
  }
  if (query.sortBy) {
    queryParams.sortBy = query.sortBy;
  }
  if (query.date) {
    queryParams.date = query.date;
  }
  if (query.itemId) {
    queryParams.itemId = query.itemId;
  }
  if (query.supportTenantId) {
    queryParams.supportTenantId = query.supportTenantId;
  }

  return await apiGet(c.getApiClient(), ofSearchItemAnalytics(c.getServiceLinks()), { queryParams }).then(result => assumeResultObject<AnalyticsSearchResults>(result, 'hits'));
};


export const api_postRestoreMedia = async (c: BackendClient, itemId: string, payload: RestoreMediaRequestDto) =>
  apiPost(c.getApiClient(), ofRestoreMedia(c.getServiceLinks(), itemId), payload);


export const api_getTransfers = async (c: BackendClient, supportTenantId?: string): Promise<TransferRequestDto[]> => {
  const queryParams: HttpQueryParameters = {};
  if (supportTenantId) {
    queryParams.supportTenantId = supportTenantId;
  }
  return apiGet(c.getApiClient(), ofTransfers(c.getServiceLinks()), {queryParams}).then((result) => assumeResultArray<TransferRequestDto>(result));
};


export const api_adminGetMosMessages = async (
  c: BackendClient,
  supportTenantId: string,
  keldaId: string,
  type: MosAdminGetMessagesType,
  queue: MosMessageQueue,
  from: string,
): Promise<MosAdminGetMessagesResponseDto> =>
  apiGet(c.getApiClient(), ofMosAdminMessages(c.getServiceLinks(), keldaId), { queryParams: { supportTenantId, from, type, queue }})
    .then((result) => assumeResultObject<MosAdminGetMessagesResponseDto>(result, 'messages'));


export const api_postMosQueueOperation = async (
  c: BackendClient,
  supportTenantId: string,
  keldaId: string,
  payload: MosAdminOperationRequestDto
): Promise<void> =>
  apiPost(c.getApiClient(), ofMosAdminMessages(c.getServiceLinks(), keldaId), payload, { queryParams: { supportTenantId }})
    .then(() => {});


/************************ transfer requests ************************/

export const api_getTransferRequests = async (
  c: BackendClient,
  storageId: string,
  ignoreLock: boolean,
  storageManagerVersion: string | undefined,
  includeSelfLocked?: boolean,
  storageManagerInstanceId?: string,
  ignoreAvailability = true,
): Promise<TransferRequestDto[]> => {
  const params: AdditionalParams = {
    queryParams: {
      ignoreLock,
      ignoreAvailability,
      includeSelfLocked: includeSelfLocked ?? false,
      storageManagerInstanceId
    }
  };
  if (storageManagerVersion !== undefined) {
    params.headers = {
      strMgrVersion: storageManagerVersion
    };
  }
  return apiGet(c.getApiClient(), ofTransferRequests(c.getServiceLinks(), storageId), params).then(result => assumeResultArray<TransferRequestDto>(result));
};


export const api_postTransferRequest = async (c: BackendClient, transferRequest: TransferRequestPostRequestDtoV2,
  storageManagerVersion: string | undefined): Promise<TransferRequestDto> => {
  const params: AdditionalParams = {};
  if (storageManagerVersion !== undefined) {
    params.headers = {
      strMgrVersion: storageManagerVersion
    };
  }

  const transferRequestCreationBody: Partial<TransferRequestPostRequestDtoV2> = pick(transferRequest, [
    'itemId',
    'isProxy',
    'artefact',
    'keldaId',
    'storageFolderName',
    'version',
    'artifactReference',
  ]);
  return apiPost(c.getApiClient(), ofTransferRequests(c.getServiceLinks(), transferRequest.storageId), transferRequestCreationBody, params).then(result => assumeResultObject<TransferRequestDto>(result, 'itemId'));
};


export const api_updateTransferRequest = async (c: BackendClient, transferRequest: TransferRequestPutRequestDto,
  storageManagerVersion: string | undefined) => {
  const params: AdditionalParams = {};
  if (storageManagerVersion !== undefined) {
    params.headers = {
      strMgrVersion: storageManagerVersion
    };
  }
  return apiPut(c.getApiClient(), ofTransferRequest(c.getServiceLinks(), transferRequest.storageId, transferRequest.id), transferRequest, params);
};


export const api_deleteTransferRequest = async (c: BackendClient, transferRequest: TransferRequestDto,
  storageManagerVersion: string | undefined, isCancelled?: boolean): Promise<void> => {
  const params: AdditionalParams = {};
  if (storageManagerVersion !== undefined) {
    params.headers = {
      strMgrVersion: storageManagerVersion
    };
  }
  const body: DeleteTransferRequestDto = {};
  if (isCancelled) {
    body.transferState = TransferStateDto.CANCELLED;
  }
  await apiDelete(c.getApiClient(), ofTransferRequest(c.getServiceLinks(), transferRequest.storageId, transferRequest.id), body, params);
};


export const api_markTransferRequest = async (c: BackendClient, storageId: string, transferRequestId: string, requestDto: MarkTransferRequestDto): Promise<string> => {
  return apiPost(c.getApiClient(), ofMarkTransferRequest(c.getServiceLinks(), storageId, transferRequestId), requestDto).then(result => assumeResultString(result));
};



/************************ persons ************************/



/**
 * Creates a blank person.
 * @param name Name of the person
 * @param description A description of the person, like for instance its job title
 */
export const api_createPerson = (c: BackendClient, name: string, description: string): Promise<any> =>
  apiPost(c.getApiClient(), ofPersons(c.getServiceLinks()), { name, description: description || undefined }).then(result => vagueResult(result));


export const api_getPerson = (c: BackendClient, personId: string, queryParams: HttpQueryParameters = {}): Promise<any> =>
  apiGet(c.getApiClient(), ofPerson(c.getServiceLinks(), personId), { queryParams }).then(result => vagueResult(result));


export const api_deletePerson = (c: BackendClient, personId: string, immediate: boolean): Promise<any> => {
  const additionalParams = immediate ? { queryParams: { immediate }} : undefined;
  return apiDelete(c.getApiClient(), ofPerson(c.getServiceLinks(), personId), {}, additionalParams);
};


export const api_updatePerson = (c: BackendClient,
  id: string, name: string, description: string, primaryImageId?: string): Promise<any> =>
  apiPost(c.getApiClient(), ofPerson(c.getServiceLinks(), id), { id, name, primaryImageId, description: description || undefined }).then(result => vagueResult(result));


export const api_similarPersons = (c: BackendClient, personId: string): Promise<SimilarPersonsDto> => {
  const path = getPath(c.getServiceLinks().personSimilar, { personId });
  return apiGet(c.getApiClient(), path).then(res => {
    const wrapped = assumeResultObject<{ data: SimilarPersonsDto }>(res, 'data');
    return wrapped.data;
  });
};


export const api_mergePersons = (c: BackendClient, sourcePersonId: string, targetPersonId: string): Promise<void> => {
  const path = getPath(c.getServiceLinks().personMerge, { personId: sourcePersonId });
  const params: AdditionalParams = {
    queryParams: {
      targetPerson: targetPersonId,
    }
  };
  return apiPost(c.getApiClient(), path, undefined, params).then(() => {});
};


/**
 * Creates a blank person picture object
 */
export const api_createPersonImage = (c: BackendClient, personId: string, fileName: string): Promise<PersonImageDto> =>
  apiPost(c.getApiClient(), ofImages(c.getServiceLinks(), personId), { personId, fileName }).then(
    result => assumeResultObject<PersonImageDto>(result, 'id'),
  );


/**
 * Get the images for a given person.
 */
export const api_getPersonImages = (c: BackendClient, personId: string): Promise<any> => {
  const queryParams: HttpQueryParameters = {};
  return apiGet(c.getApiClient(), ofImages(c.getServiceLinks(), personId), { queryParams }).then(result => vagueResult(result));
};


/**
 * Returns details about a specific image for the given person.
 */
export const api_getPersonImage = (c: BackendClient, personId: string, personImageId: string): Promise<PersonImageDto> =>
  apiGet(c.getApiClient(), ofImage(c.getServiceLinks(), personId, personImageId)).then(result => assumeResultObject<PersonImageDto>(result, 'id'));


export const api_deletePersonImage = (c: BackendClient, personId: string, personImageId: string): Promise<any> =>
  apiDelete(c.getApiClient(), ofImage(c.getServiceLinks(), personId, personImageId)).then(result => vagueResult(result));



/************************ admin stuffs ************************/

export const api_getLatestLogId = (c: BackendClient): Promise<string> =>
  apiGet(c.getApiClient(), ofChangelogs(c.getServiceLinks()), { queryParams: { latestId: true } }).then(result => assumeResultString(result));

export const api_deleteChangelog = async (c: BackendClient, id: string): Promise<void> => {
  await apiDelete(c.getApiClient(), ofChangelogs(c.getServiceLinks()), { id });
};

export const api_getSsoConfig = async (c: BackendClient): Promise<Record<string, string>> => {
  return await apiGet(c.getApiClient(), ofSsoConfig(c.getServiceLinks())).then(result => assumeObject(result.data));
};

export const api_saveSsoConfig = async (c: BackendClient, config: Record<string, string>): Promise<void> => {
  await apiPut(c.getApiClient(), ofSsoConfig(c.getServiceLinks()), config);
};

export const api_postChangelog = (c: BackendClient, payload: CreateChangelogDto): Promise<ChangelogDto> =>
  apiPost(c.getApiClient(), ofChangelogs(c.getServiceLinks()), payload).then(result => assumeResultObject<ChangelogDto>(result, 'html'));

export const api_getChangelog = (c: BackendClient, id: string): Promise<ChangelogDto> =>
  apiGet(c.getApiClient(), ofChangelogs(c.getServiceLinks()), { queryParams: { id } }).then(result => assumeResultObject<ChangelogDto>(result, 'html'));

export const api_updateChangelog = (c: BackendClient, dto: UpdateChangelogDto): Promise<ChangelogDto> =>
  apiPut(c.getApiClient(), ofChangelogs(c.getServiceLinks()), dto).then(result => assumeResultObject<ChangelogDto>(result, 'html'));

export const api_getChangelogs = (c: BackendClient, queryParams: HttpQueryParameters = {}): Promise<ChangelogDto[]> =>
  apiGet(c.getApiClient(), ofChangelogs(c.getServiceLinks()), { queryParams }).then(result => assumeResultArray<ChangelogDto>(result));

export const api_getMeteringInfo = (c: BackendClient, queryParams: HttpQueryParameters = {}, additionalParams: AdditionalParams = {}): Promise<any> =>
  apiGet(c.getApiClient(), ofMetering(c.getServiceLinks()), { queryParams, ...additionalParams }).then(result => vagueResult(result));

export const api_getTenants = (c: BackendClient, queryParams: HttpQueryParameters = {}): Promise<TenantSlimDto[]> =>
  apiGet(c.getApiClient(), ofTenants(c.getServiceLinks()), { queryParams }).then(result => assumeEmbedCollection<TenantSlimDto>(result));

export const api_getTenantIds = (c: BackendClient): Promise<TenantIdsDto> =>
  apiGet(c.getApiClient(), ofTenantIds(c.getServiceLinks())).then(result => assumeResultObject<TenantIdsDto>(result));

export const api_createTenant = (c: BackendClient, payload: CreateTenantRequestDto): Promise<any> =>
  apiPost(c.getApiClient(), ofTenants(c.getServiceLinks()), payload).then(result => vagueResult(result));

export const api_getFeatureFlags = (c: BackendClient): Promise<FeatureFlagDto[]> =>
  apiGet(c.getApiClient(), ofFeatureFlags(c.getServiceLinks())).then(result => assumeResultArray<FeatureFlagDto>(result));

export const api_updateFeatureFlag = (c: BackendClient, id: string, dto: UpdateFeatureFlagDto): Promise<FeatureFlagDto> =>
  apiPut(c.getApiClient(), ofFeatureFlag(c.getServiceLinks(), id), dto).then(result => assumeResultObject<FeatureFlagDto>(result, 'tenants_with_access'));

export const api_deleteBookmark = (c: BackendClient, id: string): Promise<void> =>
  apiDelete(c.getApiClient(), ofBookmark(c.getServiceLinks(), id)).then(v => {});

export const api_postBookmark = (c: BackendClient, payload: CreateBookmarkDto): Promise<BookmarkDto> =>
  apiPost(c.getApiClient(), ofBookmarks(c.getServiceLinks()), payload).then(result => assumeResultObject<BookmarkDto>(result, 'id'));

export const api_getBookmarks = (c: BackendClient): Promise<BookmarkDto[]> =>
  apiGet(c.getApiClient(), ofBookmarks(c.getServiceLinks())).then(result => assumeResultArray<BookmarkDto>(result));

export const api_updateBookmark = (c: BackendClient, id: string, dto: UpdateBookmarkDto): Promise<BookmarkDto> =>
  apiPut(c.getApiClient(), ofBookmark(c.getServiceLinks(), id), dto).then(result => assumeResultObject<BookmarkDto>(result, 'id'));

export const api_postBookmarkV2 = async (c: BackendClient, payload: CreateBookmarkDto): Promise<BookmarkDto> => {
  const additional: AdditionalParams = {
    queryParams: {
      version: 2
    }
  };
  return apiPost(c.getApiClient(), ofBookmarks(c.getServiceLinks()), payload, additional).then(result => assumeResultObject<BookmarkDto>(result, 'id'));
};

export const api_getBookmarksV2 = async (c: BackendClient): Promise<BookmarkDto[]> => {
  const additional: AdditionalParams = {
    queryParams: {
      version: 2
    }
  };
  return apiGet(c.getApiClient(), ofBookmarks(c.getServiceLinks()), additional).then(result => assumeResultArray<BookmarkDto>(result));
};

export const api_updateBookmarkV2 = async (c: BackendClient, id: string, dto: UpdateBookmarkDto): Promise<BookmarkDto> => {
  const additional: AdditionalParams = {
    queryParams: {
      version: 2
    }
  };
  return apiPut(c.getApiClient(), ofBookmark(c.getServiceLinks(), id), dto, additional).then(result => assumeResultObject<BookmarkDto>(result, 'id'));
};

const handleBookmarkFolderResponse = (
  result: ApiCallResult
): BookmarkFolderWithEtag => {
  const etag = result.headers!.etag!;
  const bookmarkFolderEntity = assumeResultObject<BookmarkFolderEntity>(result, 'folders');
  return {
    folders: bookmarkFolderEntity.folders,
    eTag: etag
  };
};

export const api_readBookmarkFolders = (c: BackendClient): Promise<BookmarkFolderWithEtag> =>
  apiGet(c.getApiClient(), ofBookmarkFolder(c.getServiceLinks())).then(result => {
    return handleBookmarkFolderResponse(result);
  });

export const api_writeBookmarkFolders = (c: BackendClient, payload: BookmarkWriteDto, etag: string): Promise<BookmarkFolderWithEtag> =>
  apiPut(c.getApiClient(), ofBookmarkFolder(c.getServiceLinks()), payload, { headers: {'if-match': etag }}).then(result => {
    return handleBookmarkFolderResponse(result);
  });

export const api_deleteBookmarkFolders = (c: BackendClient, payload: DeleteBookmarkFolderDto, etag: string): Promise<BookmarkFolderWithEtag> =>
  apiDelete(c.getApiClient(), ofBookmarkFolder(c.getServiceLinks()), payload, { headers: {'if-match': etag }}).then(result => {
    return handleBookmarkFolderResponse(result);
  });

export const api_getTenant = (c: BackendClient, tenantId: string, checkAvailability: boolean = false): Promise<TenantModel | Availability> =>
  apiGet(c.getApiClient(), ofTenant(c.getServiceLinks(), tenantId), { queryParams: { checkAvailability }}).then(result => assumeResultObject<TenantModel | Availability>(result));

export const api_updateTenantSettings = (c: BackendClient, tenantId: string, settings: TenantConfigDto): Promise<TenantConfigDto> =>
  apiPut(c.getApiClient(), ofTenantSettings(c.getServiceLinks(), tenantId), settings).then(result => assumeResultObject<TenantConfigDto>(result, 'featureFlags'));

export const api_createTenantAdmin = (c: BackendClient, payload: TenantAdminPostRequestDto): Promise<UserDto> =>
  apiPost(c.getApiClient(), ofTenantAdmins(c.getServiceLinks()), payload).then(result => assumeResultObject<UserDto>(result, 'groups'));


/**
 * Returns the configuration for the current tenant.
 */
export const api_getTenantConfig = (c: BackendClient, additionalParams: AdditionalParams = {}): Promise<TenantConfigDto> =>
  apiGet(c.getApiClient(), ofSettings(c.getServiceLinks()), additionalParams).then(result => assumeResultObject<TenantConfigDto>(result, 'featureFlags'));


/**
 * Updates the configuration of the current tenant.
 */
export const api_saveTenantConfig = (c: BackendClient, settings: UpdateTenantConfigDto): Promise<TenantConfigDto> =>
  apiPost(c.getApiClient(), ofSettings(c.getServiceLinks()), settings).then(result => assumeResultObject<TenantConfigDto>(result, 'featureFlags'));

export const api_updateTenantConfig = (c: BackendClient, settings: UpdateTenantConfigDto): Promise<TenantConfigDto> =>
  apiPost(c.getApiClient(), ofSettings(c.getServiceLinks()), settings).then(result => assumeResultObject<TenantConfigDto>(result, 'featureFlags'));

export const api_updateTenantColdStorageConfig = (c: BackendClient, settings: ArchiveConfig): Promise<ArchiveConfig> =>
  apiPost(c.getApiClient(), ofTenantConfigColdStorage(c.getServiceLinks()), settings).then(result => assumeResultObject<ArchiveConfig>(result, 'coldPeriodInDaysForGlacier'));

export const api_getBucketColdStorageConfigStatus = async (
  c: BackendClient, configs: ColdStorageApiCommonDto[]
): Promise<BucketColdStorageConfigStatusDto[]> => {
  const pathOfColdStorageConfigStatus = ofColdStorageConfigStatus(c.getServiceLinks());
  const queryParams: Record<string, string> = {
    configs: JSON.stringify(configs)
  };
  const result = await apiGet(c.getApiClient(), pathOfColdStorageConfigStatus, { queryParams });
  return assumeResultArray<BucketColdStorageConfigStatusDto>(result);
};

export const api_setBucketLifecycleConfig = async (
  c: BackendClient,
  setLifecycleConfigRequest: ColdStorageApiCommonDto
): Promise<SetLifecycleConfigResponseDto> =>
  apiPost(c.getApiClient(), ofLifecycleConfig(c.getServiceLinks()), setLifecycleConfigRequest).then(result => assumeResultObject<SetLifecycleConfigResponseDto>(result, 'isSuccessful'));

/**
 * @deprecated Just use ListUsersCommand directly.
 */
export const api_getUsers = async (c: BackendClient): Promise<UserModelView[]> => {
  return callApiCommand(c, new ListUsersCommand({}), undefined);
};


export const api_getUserDetails = (c: BackendClient, userId: string): Promise<UserDto> =>
  apiGet(c.getApiClient(), ofUser(c.getServiceLinks(), userId)).then(result => assumeResultObject<UserDto>(result, 'groups'));


export const api_createUser = async (c: BackendClient, payload: CreateUserDto) =>
  apiPost(c.getApiClient(), ofUsers(c.getServiceLinks()), payload).then(result => assumeResultObject<UserDto>(result, 'groups'));


export const api_updateUser = async (c: BackendClient, user: UpdateUserDto) =>
  apiPost(c.getApiClient(), ofUser(c.getServiceLinks(), user.id), user).then(result => assumeResultObject<UserDto>(result, 'groups'));


export const api_deleteUser = async (c: BackendClient, userId: string) =>
  apiDelete(c.getApiClient(), ofUser(c.getServiceLinks(), userId));

export const api_signOutUser = async (c: BackendClient, userId: string) =>
  apiPost(c.getApiClient(), ofUserSignOut(c.getServiceLinks(), userId));

export const api_getGroups = async (c: BackendClient): Promise<GroupModel[]> => {
  const response = await apiGet(c.getApiClient(), ofGroups(c.getServiceLinks()));

  if (isObject(response) && 'data' in response) {
    return getEmbed<GroupModel>(response['data'], 'collection', []);
  }
  throw new Error('Unable to fetch groups');
};

export const api_createGroup = async (c: BackendClient, group: CreateGroupDto): Promise<GroupModel> =>
  apiPost(c.getApiClient(), ofGroups(c.getServiceLinks()), group).then(result => assumeResultObject<GroupModel>(result, 'id'));


export const api_updateGroup = async (c: BackendClient, group: UpdateGroupDto): Promise<GroupModel> =>
  apiPut(c.getApiClient(), ofGroup(c.getServiceLinks(), group.id), group).then(result => assumeResultObject<GroupModel>(result, 'id'));


export const api_deleteGroup = async (c: BackendClient, groupId: string) =>
  apiDelete(c.getApiClient(), ofGroup(c.getServiceLinks(), groupId));


export const api_getTenantConfigExportLocations = async (c: BackendClient): Promise<ClientExternalLocationDto | null> =>
  apiGet(c.getApiClient(), ofTenantConfigDefaultExportLocation(c.getServiceLinks())).then(result => assumeResultObjectOrNull<ClientExternalLocationDto>(result, 'areCredentialsSaved'));


export const api_saveTenantConfigExportLocation = async (c: BackendClient, exportLocation: UpdateExternalLocationDto): Promise<UpdateExternalLocationDto> =>
  apiPost(c.getApiClient(), ofTenantConfigDefaultExportLocation(c.getServiceLinks()), exportLocation).then(result => assumeResultObject<UpdateExternalLocationDto>(result, 'accessType'));


export const api_deleteTenantConfigDefaultExportLocation = async (c: BackendClient) =>
  apiDelete(c.getApiClient(), ofTenantConfigDefaultExportLocation(c.getServiceLinks()));


export const api_getTenantConfigDefaultUploadLocation = async (c: BackendClient): Promise<UploadLocationClientDto | null> =>
  apiGet(c.getApiClient(), ofTenantConfigDefaultUploadLocation(c.getServiceLinks())).then(result => assumeResultObjectOrNull<UploadLocationClientDto>(result));

/**
 * @deprecated Just use ListUploadLocationsCommand directly.
 */
export const api_getTenantConfigUploadLocations = async (c: BackendClient): Promise<(IngestStorageConfigClientDto | MinioConfigDto)[]> =>
  callApiCommand(c, new ListUploadLocationsCommand({}), undefined);

export const api_getGeneratedMediaLocations = async (c: BackendClient): Promise<GeneratedMediaLocationDto[]> =>
  apiGet(c.getApiClient(), ofGeneratedMediaLocations(c.getServiceLinks())).then(result => assumeResultArray<GeneratedMediaLocationDto>(result));


export const api_setTenantConfigDefaultUploadLocation = async (c: BackendClient, uploadLocation: UpdateDefaultUploadLocationDto): Promise<unknown> =>
  apiPost(c.getApiClient(), ofTenantConfigDefaultUploadLocation(c.getServiceLinks()), uploadLocation);


export const api_createUploadLocation = async (c: BackendClient, dto: CreateUploadLocationDto): Promise<IngestStorageConfigClientDto> =>
  apiPost(c.getApiClient(), ofIngestStorageConfigs(c.getServiceLinks()), dto).then(result => assumeResultObject<IngestStorageConfigClientDto>(result, 'analyticConfig'));


export const api_createGeneratedMediaLocation = async (c: BackendClient, dto: CreateGeneratedMediaLocationDto): Promise<GeneratedMediaLocationDto> =>
  apiPost(c.getApiClient(), ofGeneratedMediaLocations(c.getServiceLinks()), dto).then(result => assumeResultObject<GeneratedMediaLocationDto>(result, 'bucketName'));


export const api_getGeneratedMediaLocation = async (c: BackendClient, id: string): Promise<GeneratedMediaLocationDto> =>
  apiGet(c.getApiClient(), ofGeneratedMediaLocation(c.getServiceLinks(), encodeURIComponent(id))).then(result => assumeResultObject<GeneratedMediaLocationDto>(result, 'bucketName'));


export const api_deleteGeneratedMediaLocation = async (c: BackendClient, id: string): Promise<string> =>
  apiDelete(c.getApiClient(), ofGeneratedMediaLocation(c.getServiceLinks(), encodeURIComponent(id))).then(result => assumeResultString(result));


export const api_updateGeneratedMediaLocation = async (c: BackendClient, id: string, dto: UpdateGeneratedMediaLocationDto): Promise<GeneratedMediaLocationDto> =>
  apiPut(c.getApiClient(), ofGeneratedMediaLocation(c.getServiceLinks(), encodeURIComponent(id)), dto).then(result => assumeResultObject<GeneratedMediaLocationDto>(result, 'bucketName'));

export const api_getTenantConfigDisplayAspectRatios = async (c: BackendClient): Promise<DisplayAspectRatio[]> =>
  apiGet(c.getApiClient(), ofTenantConfigDisplayAspectRatio(c.getServiceLinks())).then(result => assumeResultArray<DisplayAspectRatio>(result));


export const api_getStorageManagers = (c: BackendClient): Promise<any> =>
  apiGet(c.getApiClient(), ofStorageManagers(c.getServiceLinks())).then(result => vagueResult(result));


export const api_getTenantStorages = (c: BackendClient): Promise<any> =>
  apiGet(c.getApiClient(), ofTenantStorages(c.getServiceLinks())).then(result => vagueResult(result));


export const api_createStorage = async (c: BackendClient, storage: StorageType) =>
  apiPost(c.getApiClient(), ofTenantStorages(c.getServiceLinks()), storage);


export const api_getTenantStorage = (c: BackendClient, storageId: string): Promise<StorageType> =>
  apiGet(c.getApiClient(), ofTenantStorage(c.getServiceLinks(), storageId)).then(result => assumeResultObject<StorageType>(result, 'name'));


export const api_deleteStorage = async (c: BackendClient, storageId: string) =>
  apiDelete(c.getApiClient(), ofTenantStorage(c.getServiceLinks(), storageId));


export const api_updateStorage = async (c: BackendClient, storageId: string, storage: StorageType) =>
  apiPut(c.getApiClient(), ofTenantStorage(c.getServiceLinks(), storageId), storage);


export const api_getLiveSourcesV2 = (c: BackendClient): Promise<LiveSourceDtoV2[]> =>
  apiGet(c.getApiClient(), ofLiveSourcesV2(c.getServiceLinks())).then(result => assumeResultArray<LiveSourceDtoV2>(result));


export const api_createLiveSourceV2 = async (c: BackendClient, liveSource: UpdateLiveSourceDtoV2) =>
  apiPost(c.getApiClient(), ofLiveSourcesV2(c.getServiceLinks()), liveSource);


export const api_deleteLiveSourceV2 = async (c: BackendClient, liveSourceId: string) =>
  apiDelete(c.getApiClient(), ofLiveSourceV2(c.getServiceLinks(), liveSourceId));


export const api_updateLiveSourceV2 = async (c: BackendClient, liveSourceId: string, liveSource: UpdateLiveSourceDtoV2) =>
  apiPut(c.getApiClient(), ofLiveSourceV2(c.getServiceLinks(), liveSourceId), liveSource);


export const api_getRouters = (c: BackendClient): Promise<RouterDto[]> =>
  apiGet(c.getApiClient(), ofRouters(c.getServiceLinks())).then(result => assumeResultArray<RouterDto>(result));


export const api_createRouter = async (c: BackendClient, router: UpdateRouterDto): Promise<RouterDto> =>
  apiPost(c.getApiClient(), ofRouters(c.getServiceLinks()), router).then(result => assumeResultObject<RouterDto>(result, 'displayName'));

export const api_getRouter = async (c: BackendClient, routerId: string): Promise<RouterDto> =>
  apiGet(c.getApiClient(), ofRouter(c.getServiceLinks(), routerId)).then(result => assumeResultObject<RouterDto>(result));

export const api_deleteRouter = async (c: BackendClient, routerId: string) =>
  apiDelete(c.getApiClient(), ofRouter(c.getServiceLinks(), routerId));


export const api_updateRouter = async (c: BackendClient, routerId: string, router: UpdateRouterDto) =>
  apiPut(c.getApiClient(), ofRouter(c.getServiceLinks(), routerId), router);


export const api_postRouterConnectionRequest = async (c: BackendClient, routerId: string, req: CrosspointConnectionRequest) =>
  apiPost(c.getApiClient(), ofRouterCrosspoints(c.getServiceLinks(), routerId), req);


export const api_postRouterMessage = async (c: BackendClient, routerId: string, msg: RouterMessageDto) =>
  apiPost(c.getApiClient(), ofRouterMessages(c.getServiceLinks(), routerId), msg);

export const api_getRouterSources = async (c: BackendClient, routerId: string): Promise<RouterSourceDto[]> =>
  apiGet(c.getApiClient(), ofRouterSources(c.getServiceLinks(), routerId)).then(result => assumeResultArray<RouterSourceDto>(result));


export const api_setSelectedNdiSourceForPipeline = async (c: BackendClient, liveSourceId: string, pipelineId: string, dto: UpdateNdiCrosspointDto) =>
  apiPut(c.getApiClient(), ofNdiRouter(c.getServiceLinks(), liveSourceId, pipelineId), dto);

export const api_setSelectedDynamicSourceForPipeline = async (c: BackendClient, liveSourceId: string, pipelineId: string, dto: UpdateSelectedDynamicSourceDto) =>
  apiPut(c.getApiClient(), ofMediaPipelineRouter(c.getServiceLinks(), liveSourceId, pipelineId), dto);

export const api_getSelectedDynamicSourceForPipeline = async (c: BackendClient, liveSourceId: string, pipelineId: string): Promise<GetSelecteDynamicSourceDto> =>
  apiGet(c.getApiClient(), ofMediaPipelineRouter(c.getServiceLinks(), liveSourceId, pipelineId)).then(result => assumeResultObject<GetSelecteDynamicSourceDto>(result));

export const api_getKeldaMediaPipelineStatus = async (c: BackendClient, liveSourceId: string, pipelineId: string): Promise<GetKeldaMediaPipelineStatusDto | null> =>
  apiGet(c.getApiClient(), ofKeldaMediaPipelineStatus(c.getServiceLinks(), liveSourceId, pipelineId)).then(result => assumeResultObjectOrNoContent<GetKeldaMediaPipelineStatusDto>(result, 'source'));

export const api_updateKeldaMediaPipelineStatus = async (c: BackendClient, liveSourceId: string, pipelineId: string, payload: UpdateKeldaMediaPipelineStatusDto): Promise<void> =>
  apiPut(c.getApiClient(), ofKeldaMediaPipelineStatus(c.getServiceLinks(), liveSourceId, pipelineId), payload).then((_) => { });

export const api_getExpectedLivePipelines = async (c: BackendClient): Promise<ExpectedPipelineDto[]> =>
  apiGet(c.getApiClient(), ofLivePipelines(c.getServiceLinks())).then(result => assumeResultArray<ExpectedPipelineDto>(result));


export const api_getLiveSourceStatus = async (c: BackendClient, liveSource: LiveSourceDtoV2): Promise<LiveSourceStatus> =>
  apiPost(c.getApiClient(), ofLivePipelines(c.getServiceLinks()), liveSource).then(result => assumeResultObject<LiveSourceStatus>(result, 'status'));


export const api_getSystemConfigTranscriptionLanguageSettings = async (c: BackendClient) =>
  apiGet(c.getApiClient(), ofSystemConfigTranscriptionLanguageSettings(c.getServiceLinks()));


export const api_getAllRouterSources = async (c: BackendClient): Promise<Map<string, RouterSourceDto[]>> =>
  apiGet(c.getApiClient(), ofAllRouterSources(c.getServiceLinks())).then(response => {
    const sources = assumeResultObject<Record<string, RouterSourceDto[]>>(response);
    return new Map(Object.entries(sources));
  });


export const api_getNdiSourcesForTenant = async (c: BackendClient): Promise<NdiSourcesForTenantDto> =>
  apiGet(c.getApiClient(), ofNdiSources(c.getServiceLinks())).then(response => assumeResultObject<NdiSourcesForTenantDto>(response));

export const api_getDynamicSourceListForTenant = async (c: BackendClient, sourceListKey: SourceListKey): Promise<string[]> =>
  apiGet(c.getApiClient(), ofDynamicSourceList(c.getServiceLinks(), sourceListKey)).then(response => assumeResultArray<string>(response));

export const api_getAllRouterControllerStatuses = async (c: BackendClient): Promise<RouterControllerStatusDto[]> =>
  apiGet(c.getApiClient(), ofAllRouterControllerStatuses(c.getServiceLinks())).then(response => assumeResultArray<RouterControllerStatusDto>(response));


export const api_getTenantLiveLocations = async (c: BackendClient): Promise<LiveLocationDto[]> =>
  apiGet(c.getApiClient(), ofLiveLocations(c.getServiceLinks())).then(response => assumeResultArray<LiveLocationDto>(response));


export const api_createTenantLiveLocation = async (c: BackendClient, createdLocation: CreateOrUpdateLiveLocationDto): Promise<LiveLocationDto> =>
  apiPost(c.getApiClient(), ofLiveLocations(c.getServiceLinks()), createdLocation).then(response => assumeResultObject<LiveLocationDto>(response, 'timezone'));


export const api_updateTenantLiveLocation = async (c: BackendClient, updatedLocation: CreateOrUpdateLiveLocationDto, updateId: string): Promise<LiveLocationDto> =>
  apiPut(c.getApiClient(), ofLiveLocation(c.getServiceLinks(), updateId), updatedLocation).then(response => assumeResultObject<LiveLocationDto>(response, 'timezone'));


export const api_deleteTenantLiveLocation = async (c: BackendClient, deletedLocationIdentifier: string) =>
  apiDelete(c.getApiClient(), ofLiveLocation(c.getServiceLinks(), deletedLocationIdentifier));


export const api_getTenantConfigTranscriptionLanguageSettings = async (c: BackendClient, additionalParams: AdditionalParams = {}) =>
  apiGet(c.getApiClient(), ofTenantConfigTranscriptionLanguageSettings(c.getServiceLinks()), additionalParams);


export const api_updateTenantConfigTranscriptionLanguageSettings = async (c: BackendClient,
  payload: CreateTranscriptOptionDto[]) =>
  apiPost(c.getApiClient(), ofTenantConfigTranscriptionLanguageSettings(c.getServiceLinks()), payload);


export const api_getOptionListIds = async (c: BackendClient): Promise<OptionListIdsDto> => {
  const queryParams = { idsOnly: true };
  return apiGet(c.getApiClient(), ofOptionLists(c.getServiceLinks()), { queryParams }).then(result => assumeResultObject<OptionListIdsDto>(result, 'ids'));
};


export const api_getOptionList = async (c: BackendClient, id: string): Promise<OptionListDto> =>
  apiGet(c.getApiClient(), ofOptionList(c.getServiceLinks(), id)).then(result => assumeResultObject<OptionListDto>(result));


export const api_createOptionList = async (c: BackendClient, optionList: UpdateOptionListDto) =>
  apiPost(c.getApiClient(), ofOptionLists(c.getServiceLinks()), optionList);


export const api_deleteOptionList = async (c: BackendClient, optionListId: string) =>
  apiDelete(c.getApiClient(), ofOptionList(c.getServiceLinks(), optionListId));


export const api_updateOptionList = async (c: BackendClient, optionListId: string, optionList: UpdateOptionListDto) =>
  apiPut(c.getApiClient(), ofOptionList(c.getServiceLinks(), optionListId), optionList);


export const api_getPlayoutDevices = (c: BackendClient): Promise<PlayoutDeviceDto[]> =>
  apiGet(c.getApiClient(), ofPlayoutDevices(c.getServiceLinks())).then(result => assumeResultArray<PlayoutDeviceDto>(result));


export const api_getPlayoutStatus = (c: BackendClient): Promise<PlayoutDeviceStatusGetDto[]> =>
  apiGet(c.getApiClient(), ofPlayoutStatus(c.getServiceLinks())).then(result => assumeResultArray<PlayoutDeviceStatusGetDto>(result));


export const api_sendPlayoutCommand = (c: BackendClient, dto: KeldaPlayoutCommandDto | ResetPlayoutDto): Promise<void> =>
  apiPost(c.getApiClient(), ofPlayoutCommands(c.getServiceLinks()), dto).then(() => {});


export const api_postDeduplicationRequest = async (c: BackendClient, dto: DeduplicationRequestDto): Promise<DeduplicationResultDto> =>
  apiPost(c.getApiClient(), ofDeduplication(c.getServiceLinks()), dto).then(response => assumeResultObject<DeduplicationResultDto>(response, 'items'));



export const api_updateFolderMetadata = async (c: BackendClient, folderId: string, payload: FolderMetadataUpdatePayload): Promise<FolderMetadataDto> =>
  apiPut(c.getApiClient(), ofUpdateFolderMetadata(c.getServiceLinks(), folderId), payload)
    .then(result => assumeResultObject<FolderMetadataDto>(result));

/************************ mdfs ************************/

export const api_getMdfs = (c: BackendClient, params: GetMdfsParams): Promise<any> => {
  const includeFields = params?.includeFields ?? true;
  const includeViews = params?.includeViews ?? true;
  return apiGet(c.getApiClient(), ofMdfs(c.getServiceLinks()), { queryParams: { includeFields, includeViews }})
    .then(result => vagueResult(result));
};

export type GetMdfsParams = {
  includeFields: boolean,
  includeViews: boolean,
};

export const api_createMdf = (c: BackendClient, request: MdfCreationRequest): Promise<MdfEntity> =>
  apiPost(c.getApiClient(), ofMdfs(c.getServiceLinks()), request).then(result => assumeResultObject<MdfEntity>(result, 'label'));


export const api_getMdf = (c: BackendClient, mdfId: string): Promise<MdfEntity> =>
  apiGet(c.getApiClient(), ofMdf(c.getServiceLinks(), encodeURIComponent(mdfId))).then(result => assumeResultObject<MdfEntity>(result, 'label'));


export const api_deleteMdf = (c: BackendClient, mdfId: string) =>
  apiDelete(c.getApiClient(), ofMdf(c.getServiceLinks(), mdfId));


/**
 * Since mdf is an object, we need to stringify and then parse it again, otherwise
 * the AWS API gateway code will invoke toString() on it which will
 * result in something like "[object Object]".
 *
 * Just stringifying causes a bug after deployment - we need to parse it again and properly send an object and not a string through,
 * just without all the functions etc from the class object.
 */
export const api_putMdf = (c: BackendClient, mdf: MdfEntity): Promise<MdfEntity> =>
  apiPut(c.getApiClient(), ofMdf(c.getServiceLinks(), mdf.id), JSON.parse(JSON.stringify(mdf))).then(result => assumeResultObject<MdfEntity>(result, 'label'));


export const api_getTimedMetadata = async (c: BackendClient, itemId: string): Promise<TimedMetadataCollection> =>
  apiGet(c.getApiClient(), ofTimedMetadata(c.getServiceLinks(), itemId), injectDefaultQueryParamsForItemApis({})).then(result => assumeResultObject<TimedMetadataCollection>(result, 'items'));

export const api_getTimedMetadataById = async (c: BackendClient, itemId: string, timedMetadataId: string): Promise<TimedMetadataItem> =>
  apiGet(c.getApiClient(), ofTimedMetadataById(c.getServiceLinks(), itemId, timedMetadataId), injectDefaultQueryParamsForItemApis({})).then(result => assumeResultObject<TimedMetadataItem>(result, 'id'));


export const api_updateTimedMetadata = (c: BackendClient, itemId: string, update: UpdateTimedMetadataRequestDto): Promise<void> =>
  apiPut(c.getApiClient(), ofTimedMetadata(c.getServiceLinks(), itemId), update).then(() => {});


/************************ user ************************/

export const api_getUserSettings = async (c: BackendClient): Promise<UserSettings | null> =>
  apiGet(c.getApiClient(), ofUserSettings(c.getServiceLinks())).then(result => assumeResultObjectOrNoContent<UserSettings>(result, 'bookmarks'));


export const api_saveUserSettings = async (c: BackendClient, settings: UserSettings): Promise<UserSettings> =>
  apiPut(c.getApiClient(), ofUserSettings(c.getServiceLinks()), settings).then(result => assumeResultObject<UserSettings>(result, 'bookmarks'));


export const api_createAvatarUploadURL = async (c: BackendClient, dto: UserAvatarUploadUrlRequest): Promise<UserAvatarUploadDto> =>
  apiPost(c.getApiClient(), ofAvatar(c.getServiceLinks()), dto).then(result => assumeResultObject<UserAvatarUploadDto>(result, 'uploadUrl'));


export const api_deleteUserAvatar = async (c: BackendClient): Promise<void> =>
  apiDelete(c.getApiClient(), ofAvatar(c.getServiceLinks())).then(() => {});


export const api_getAllUsers = async (c: BackendClient): Promise<UserInfoDto[]> =>
  apiGet(c.getApiClient(), ofUserInfo(c.getServiceLinks())).then(result => assumeResultArray<UserInfoDto>(result));

export const api_getUserStatus = async (c: BackendClient): Promise<UserStatusDto> =>
  apiGet(c.getApiClient(), ofUserStatus(c.getServiceLinks())).then(result => assumeResultObject<UserStatusDto>(result, 'isDeactivated'));

/************************ stats ************************/

export const api_getStatistics = async (c: BackendClient, params: GetCountStatisticsQueryParams = {}): Promise<CountStatisticsResponseDto> => {
  return await apiGet(c.getApiClient(), ofStats(c.getServiceLinks()), {queryParams: params}).then(result => assumeResultObject<CountStatisticsResponseDto>(result, 'dateRange'));
};


export const api_logStatistic = async (c: BackendClient, body: LogStaticRequestDto): Promise<void> => {
  await apiPost(c.getApiClient(), ofStats(c.getServiceLinks()), body);
};

/*********************
 * IngestStorageConfig
 */
export const api_getIngestStorageConfigs = async (c: BackendClient): Promise<IngestStorageConfigClientDto[]> =>
  apiGet(c.getApiClient(), ofIngestStorageConfigs(c.getServiceLinks())).then(result => assumeResultArray<IngestStorageConfigClientDto>(result));


export const api_getIngestStorageConfig = async (c: BackendClient, itemId: string): Promise<IngestStorageConfigClientDto> =>
  apiGet(c.getApiClient(), ofIngestStorageConfig(c.getServiceLinks(), itemId)).then(result => assumeResultObject<IngestStorageConfigClientDto>(result, 'analyticConfig'));


export const api_createIngestStorageConfig = async (c: BackendClient, body: IngestStorageConfigClientDto): Promise<IngestStorageConfigClientDto> =>
  apiPost(c.getApiClient(), ofIngestStorageConfigs(c.getServiceLinks()), body).then(result => assumeResultObject<IngestStorageConfigClientDto>(result, 'analyticConfig'));


export const api_updateIngestStorageConfig = async (c: BackendClient, itemId: string, body: IngestStorageConfigClientDto | UpdateUploadLocationDto): Promise<IngestStorageConfigClientDto> =>
  apiPut(c.getApiClient(), ofIngestStorageConfig(c.getServiceLinks(), itemId), body).then(result => assumeResultObject<IngestStorageConfigClientDto>(result, 'analyticConfig'));


export const api_deleteIngestStorageConfig = async (c: BackendClient, itemId: string): Promise<string> =>
  apiDelete(c.getApiClient(), ofIngestStorageConfig(c.getServiceLinks(), itemId)).then(result => assumeResultString(result));


export const api_postRecordLog = async (c: BackendClient, body: any): Promise<void> =>
  apiPost(c.getApiClient(), ofRecordLog(c.getServiceLinks()), body).then(() => {});


export const api_postMosMessage = async (c: BackendClient, keldaId: string, body: MosRequestFromNcsDto | MosResponseFromNcsDto | MosLockReleaseRequestDto): Promise<MosResponseFromBackendDto> =>
  apiPost(c.getApiClient(), ofMos(c.getServiceLinks(), keldaId), body).then(result => assumeResultObject<MosResponseFromBackendDto>(result));


export const api_getNextMosMessage = async (c: BackendClient, keldaId: string,
  queue: MosMessageQueue, workerId: string): Promise<MosGetNextMessageResponseDto> =>
  apiGet(c.getApiClient(), ofNextMosMessage(c.getServiceLinks(), keldaId, queue), { queryParams: { workerId } }).then(result => assumeResultObject<MosGetNextMessageResponseDto>(result));


export const api_getPackages = async (c: BackendClient, id: string, keldaVersion: string | undefined): Promise<PackageDefinition[]> =>
  apiGet(c.getApiClient(), ofPackages(c.getServiceLinks(), id), { queryParams: { keldaVersion }}).then(result => assumeResultArray<PackageDefinition>(result));


/**
 * @deprecated Just use CreateLocationCommand directly.
 */
export const api_createLocationConfig = async (c: BackendClient, body: CreateMinioConfigDto, keldaConfigId?: string): Promise<MinioConfigDto> =>{
  return callApiCommand(c, new CreateLocationCommand({ keldaConfigId, config: body }), undefined);
};

export const api_updateLocationConfig = async (c: BackendClient, updateDto: UpdateMinioConfigDto): Promise<MinioConfigDto> =>
  apiPut(c.getApiClient(), ofConfig(c.getServiceLinks(), 'location', updateDto.id), updateDto).then(result => assumeResultObject<MinioConfigDto>(result, 'endpoint'));

/**
 * @deprecated Just use DeleteLocationCommand directly.
 */
export const api_deleteLocationConfig = async (c: BackendClient, configId: string): Promise<void> =>
  callApiCommand(c, new DeleteLocationCommand({ configId }), undefined);


/************************ Storage location verification ************************/
export const api_verifyExternalStorage = async (
  c: BackendClient,
  verificationRequest: ExternalStorageVerificationRequest
): Promise<ExternalStorageVerificationResponse> =>
  apiPost(c.getApiClient(), ofVerifyExternalStorage(c.getServiceLinks()), verificationRequest).then(result => assumeResultObject<ExternalStorageVerificationResponse>(result, 'message'));

/************************ Catch up ingest of storage ************************/
export const api_catchUpStorageIngest = async (
  c: BackendClient,
  catchUpRequest: CatchupIngestStorageRequest
): Promise<void> =>
  apiPost(c.getApiClient(), ofCatchUpStorageIngest(c.getServiceLinks()), catchUpRequest).then(() => {});


/************************ ingest file from external storages  ************************/
export const api_ingestFile = async (
  c: BackendClient,
  body: IngestFileDto
): Promise<void> =>
  apiPost(c.getApiClient(), ofIngestFile(c.getServiceLinks()), body).then(() => {});

/************************ Live recording ************************/
export const api_createLiveRecordingItem = (c: BackendClient, dto: NonScheduledLiveItemCreationRequestDto): Promise<NonScheduledLiveItemCreationResponseDto> =>
  apiPost(c.getApiClient(), ofLiveItems(c.getServiceLinks()), dto).then(result => assumeResultObject<NonScheduledLiveItemCreationResponseDto>(result, 'liveRecording'));


export const api_getLiveItemRecordingState = (c: BackendClient, itemId: string, supportTenantId?: string): Promise<LiveRecordingItemStateDto> => {
  const queryParams: HttpQueryParameters = {};
  if (supportTenantId) {
    queryParams.supportTenantId = supportTenantId;
  }
  return apiGet(c.getApiClient(), ofLiveItem(c.getServiceLinks(), itemId), { queryParams }).then(result => assumeResultObject<LiveRecordingItemStateDto>(result, 'state'));
};


export const api_performLiveItemAction = (c: BackendClient, itemId: string, dto: LiveItemStateRequestDto, supportTenantId?: string): Promise<LiveRecordingItemStateDto> => {
  const body = supportTenantId ? { ...dto, supportTenantId } : dto;
  return apiPost(c.getApiClient(), ofLiveItem(c.getServiceLinks(), itemId), body).then(result => assumeResultObject<LiveRecordingItemStateDto>(result, 'state'));
};


export const api_appendRetrievedSegment = (c: BackendClient, itemId: string, dto: RetrievedSegmentRangeDto): Promise<void> =>
  apiPost(c.getApiClient(), ofItemExternalHighresInfoAppendRetrievedSegment(c.getServiceLinks(), itemId), dto).then(() => {});


export const api_getExternalHighresInfo = (c: BackendClient, itemId: string): Promise<ExternalHighresInfoDto | null> =>
  apiGet(c.getApiClient(), ofItemExternalHighresInfo(c.getServiceLinks(), itemId)).then(result => assumeResultObjectOrNull<ExternalHighresInfoDto>(result, 'recordingId'));

export async function api_getPendingOfflineStorageJobs(c: BackendClient, pluginId: string): Promise<GetOfflineStoragePendingJobsDto> {
  const additionalParams = {
    queryParams: { status: 'pending' },
  };
  const ret = await apiGet(c.getApiClient(), ofOfflineStorageJobs(c.getServiceLinks(), pluginId), additionalParams).then(result => assumeResultObject<GetOfflineStoragePendingJobsDto>(result, 'type'));
  return ret;
}

export async function api_getStartedOfflineStorageJobs(c: BackendClient, pluginId: string): Promise<GetOfflineStorageStartedJobsDto> {
  const additionalParams = {
    queryParams: { status: 'started' },
  };
  const ret = await apiGet(c.getApiClient(), ofOfflineStorageJobs(c.getServiceLinks(), pluginId), additionalParams).then(result => assumeResultObject<GetOfflineStorageStartedJobsDto>(result, 'type'));
  return ret;
}

export async function api_updateOfflineStorageJob(c: BackendClient, jobId: string, update: OfflineStorageJobUpdate): Promise<void> {
  await apiPut(c.getApiClient(), ofOfflineStorageJob(c.getServiceLinks(), jobId), update).then(() => {});
}

/**
 * This is used when we report pipeline status from Kelda, such as for
 * Elemental proxy and VDCP.
 */
export const api_reportPipelineActionResult = (c: BackendClient, itemId: string, dto: PipelineActionResultDto): Promise<void> => {
  return apiPost(c.getApiClient(), ofLiveItem(c.getServiceLinks(), itemId), dto).then(() => {});
};


export const api_createScheduledRecording = (c: BackendClient, dto: ScheduledRecordingCreationRequestDto): Promise<ScheduledRecordingDto> =>
  apiPost(c.getApiClient(), ofScheduledRecordings(c.getServiceLinks()), dto).then(result => assumeResultObject<ScheduledRecordingDto>(result, 'startTimeMs'));


export const api_getScheduledRecording = (c: BackendClient, itemId: string): Promise<ScheduledRecordingDto> =>
  apiGet(c.getApiClient(), ofScheduledRecording(c.getServiceLinks(), itemId)).then(result => assumeResultObject<ScheduledRecordingDto>(result, 'startTimeMs'));


export const api_postQcEvents = (c: BackendClient, itemId: string, payload: QcEventsUpdateDto): Promise<void> =>
  apiPost(c.getApiClient(), ofQc(c.getServiceLinks(), itemId), payload).then(() => {});


export const api_putScheduledRecording = (c: BackendClient, itemId: string, dto: ScheduledRecordingPutRequestDto): Promise<ScheduledRecordingDto> =>
  apiPut(c.getApiClient(), ofScheduledRecording(c.getServiceLinks(), itemId), dto).then(result => assumeResultObject<ScheduledRecordingDto>(result, 'startTimeMs'));


export const api_deleteScheduledRecording = (c: BackendClient, itemId: string): Promise<void> =>
  apiDelete(c.getApiClient(), ofScheduledRecording(c.getServiceLinks(), itemId)).then(v => {});


export const api_getScheduledRecordings = (c: BackendClient, from: number, to: number): Promise<GetScheduledRecordingsDto> =>
  apiGet(c.getApiClient(), ofScheduledRecordings(c.getServiceLinks()), {queryParams: { from, to }}).then(result => assumeResultObject<GetScheduledRecordingsDto>(result, 'recordings'));


export const api_createRecordingSeries = (c: BackendClient, dto: RecordingSeriesCreationDto): Promise<RecordingSeriesFullDto> =>
  apiPost(c.getApiClient(), ofRecordingSeriesCollection(c.getServiceLinks()), dto).then(result => assumeResultObject<RecordingSeriesFullDto>(result, 'recurrenceSchedule'));


export const api_getRecordingSeries = (c: BackendClient, seriesId: string): Promise<RecordingSeriesFullDto> =>
  apiGet(c.getApiClient(), ofRecordingSeries(c.getServiceLinks(), seriesId)).then(result => assumeResultObject<RecordingSeriesFullDto>(result, 'recurrenceSchedule'));


export const api_updateRecordingSeries = (c: BackendClient, seriesId: string, dto: RecordingSeriesUpdateDto): Promise<RecordingSeriesFullDto> =>
  apiPut(c.getApiClient(), ofRecordingSeries(c.getServiceLinks(), seriesId), dto).then(result => assumeResultObject<RecordingSeriesFullDto>(result, 'recurrenceSchedule'));


export const api_createRecordingSeriesItem = (c: BackendClient, seriesId: string, dto: RecordingSeriesItemCreationDto): Promise<ScheduledRecordingDto> =>
  apiPost(c.getApiClient(), ofRecordingSeriesItems(c.getServiceLinks(), seriesId), dto).then(result => assumeResultObject<ScheduledRecordingDto>(result, 'startTimeMs'));

export const api_deletePlannedSeriesItem = (c: BackendClient, seriesId: string, scheduledStartTimeMs: number): Promise<void> =>
  apiDelete(c.getApiClient(), ofRecordingSeriesItems(c.getServiceLinks(), seriesId), undefined, { queryParams: { scheduledStartTimeMs }}).then(result => {});

export const api_getMediaSegmentUrls = (c: BackendClient,
                itemId: string, streamIds: StreamRoleId[],
                signedForMethod: 'get' | 'put',
                firstSegmentIndex: number): Promise<MediaSegmentsResponse> => {
  const path = ofMediaSegments(
    c.getServiceLinks(),
    itemId,
    streamIds.join(','),
    signedForMethod,
    '' + firstSegmentIndex
  );
  return apiGet(c.getApiClient(), path).then(result => assumeResultObject<MediaSegmentsResponse>(result));
};


export const api_getItemEditStatuses = (c: BackendClient, itemId: string): Promise<ItemEditStatusDto[]> => {
  return apiGet(c.getApiClient(), ofItemEditStatus(c.getServiceLinks(), itemId)).then(result => assumeResultArray<ItemEditStatusDto>(result));
};


export const api_postItemEditStatus = (c: BackendClient, itemId: string, payload: ItemEditStatusCreateDto): Promise<string> => {
  return apiPost(c.getApiClient(), ofItemEditStatus(c.getServiceLinks(), itemId), payload).then(result => assumeResultString(result));
};


export const api_deleteItemEditStatus = (c: BackendClient, itemId: string, payload: ItemEditStatusDeleteDto): Promise<string> => {
  return apiDelete(c.getApiClient(), ofItemEditStatus(c.getServiceLinks(), itemId), payload).then(result => assumeResultString(result));
};

export const api_getIPAddress = async (c: BackendClient): Promise<IPAddressDto> => {
  return apiGet(c.getApiClient(), ofGetIPAddress(c.getServiceLinks())).then(result => assumeResultObject(result));
};

export const api_getShares = async (
  c: BackendClient,
  allForTenant: boolean,
  contentId?: string,
  token?: string,
  startDate?: string,
  endDate?: string
): Promise<{ shares: ExternalShareGetDto[], nextToken: string | null }> => {
  const queryParams: Record<string, string> = {};
  if (allForTenant) {
    queryParams[ALL_FOR_TENANT_PARAM] = 'true';
  }
  if (contentId) {
    queryParams[CONTENT_ID_PARAM] = contentId;
  }
  if (token) {
    queryParams['nextToken'] = token;
  }
  if (startDate) {
    queryParams['startDate'] = startDate;
  }
  if (endDate) {
    queryParams['endDate'] = endDate;
  }

  const result = await apiGet(c.getApiClient(), ofExternalShares(c.getServiceLinks()), { queryParams });

  const data = assumeResultObject<{ shares?: ExternalShareGetDto[]; nextToken?: string | null }>(result, 'shares');
  return {
    shares: data.shares || [],
    nextToken: data.nextToken || null,
  };
};


export const api_createShare = (c: BackendClient, dto: ShareCreationRequestDto): Promise<ExternalShareGetDto> =>
  apiPost(c.getApiClient(), ofExternalShares(c.getServiceLinks()), dto).then(result => assumeResultObject<ExternalShareGetDto>(result, 'emailHistory'));


export const api_getShare = (c: BackendClient, shareSecret: string): Promise<ExternalShareGetDto> =>
  apiGet(c.getApiClient(), ofExternalShare(c.getServiceLinks(), shareSecret)).then(result => assumeResultObject<ExternalShareGetDto>(result, 'emailHistory'));


export const api_updateShare = (c: BackendClient, shareSecret: string, dto: ShareUpdateRequestDto): Promise<ExternalShareGetDto> =>
  apiPut(c.getApiClient(), ofExternalShare(c.getServiceLinks(), shareSecret), dto).then(result => assumeResultObject<ExternalShareGetDto>(result, 'emailHistory'));


export const api_inviteToShare = (c: BackendClient, shareSecret: string, email: string): Promise<void> =>
  apiPost(c.getApiClient(), ofShareInvitations(c.getServiceLinks(), shareSecret), { email } satisfies ShareInvitationRequestDto).then(() => {});


export const api_deleteShare = (c: BackendClient, shareSecret: string): Promise<void> =>
  apiDelete(c.getApiClient(), ofExternalShare(c.getServiceLinks(), shareSecret)).then(() => {});

export const api_getCustomActions = (c: BackendClient): Promise<CustomActionItemDto[]> =>
  apiGet(c.getApiClient(), ofCustomActions(c.getServiceLinks())).then(result => assumeResultArray<CustomActionItemDto>(result));

export const api_createCustomAction = (c: BackendClient, dto: CreateCustomActionItemDto): Promise<CustomActionItemDto> =>
  apiPost(c.getApiClient(), ofCustomActions(c.getServiceLinks()), dto).then(result => assumeResultObject<CustomActionItemDto>(result, 'id'));

export const api_updateCustomAction = (c: BackendClient, caId: string, dto: UpdateCustomActionItemDto): Promise<void> =>
  apiPut(c.getApiClient(), ofCustomAction(c.getServiceLinks(), caId), dto).then(() => {});

export const api_deleteCustomAction = (c: BackendClient, caId: string): Promise<void> =>
  apiDelete(c.getApiClient(), ofCustomAction(c.getServiceLinks(), caId)).then(() => {});

export const api_getUserTask = async (
  c: BackendClient,
  assignee: string,
  taskId: string,
): Promise<GetUserTaskResponseDto> =>
  apiGet(c.getApiClient(), ofUserTask(assignee, taskId, c.getServiceLinks())).then(result => assumeResultObject<GetUserTaskResponseDto>(result));

export const api_getWebhooks = (c: BackendClient): Promise<WebhookDto[]> =>
  apiGet(c.getApiClient(), ofWebhooks(c.getServiceLinks())).then(result => assumeResultArray<WebhookDto>(result));

export const api_createWebhook = (c: BackendClient, dto: CreateWebhookDto): Promise<WebhookDto> =>
  apiPost(c.getApiClient(), ofWebhooks(c.getServiceLinks()), dto).then(result => assumeResultObject<WebhookDto>(result, 'id'));

export const api_updateWebhook = (c: BackendClient, whId: string, dto: UpdateWebhookDto): Promise<void> =>
  apiPut(c.getApiClient(), ofWebhook(c.getServiceLinks(), whId), dto).then(() => {});

export const api_deleteWebhook = (c: BackendClient, whId: string): Promise<void> =>
  apiDelete(c.getApiClient(), ofWebhook(c.getServiceLinks(), whId)).then(() => {});



export const api_getCustomIcons = (c: BackendClient): Promise<CustomIconDto[]> =>
  apiGet(c.getApiClient(), ofCustomIcons(c.getServiceLinks())).then(result => assumeResultArray<CustomIconDto>(result));

export const api_createCustomIcon = (c: BackendClient, dto: CreateCustomIconDto): Promise<CustomIconUploadDto> =>
  apiPost(c.getApiClient(), ofCustomIcons(c.getServiceLinks()), dto).then(result => assumeResultObject<CustomIconUploadDto>(result));

export const api_deleteCustomIcon = (c: BackendClient, iconId: string): Promise<void> =>
  apiDelete(c.getApiClient(), ofCustomIcon(c.getServiceLinks(), iconId)).then(() => {});

export const api_getTenantLogo = (c: BackendClient): Promise<TenantLogoDto> =>
  apiGet(c.getApiClient(), ofTenantLogo(c.getServiceLinks())).then(result => assumeResultObject<TenantLogoDto>(result));

export const api_initiateTenantLogoUpload = (c: BackendClient): Promise<TenantLogoUploadDto> =>
  apiPost(c.getApiClient(),  ofTenantLogo(c.getServiceLinks()), {}).then(result => assumeResultObject<TenantLogoUploadDto>(result, 'signedUploadUrl'));

export const api_deleteTenantLogo = (c: BackendClient): Promise<void> =>
  apiDelete(c.getApiClient(), ofTenantLogo(c.getServiceLinks())).then(() => {});

export const api_getUserTasks = async (
  c: BackendClient,
  assignee: string,
  queryParams: GetUserTasksQueryParams
): Promise<GetUserTasksResponseDto> =>
  apiGet(c.getApiClient(), ofUserTasks(assignee, c.getServiceLinks()), { queryParams })
    .then(result => assumeResultObject<GetUserTasksResponseDto>(result, 'tasks'));

export const api_getStorageLocations = (c: BackendClient): Promise<StorageLocationApiResponse[]> => {
  return apiGet(c.getApiClient(), ofStorages(c.getServiceLinks())).then(result => assumeResultArray<StorageLocationApiResponse>(result));
};

export const api_createStorageLocation = (c: BackendClient, payload: StorageLocationCreationDto): Promise<StorageLocationApiResponse> => {
  return apiPost(c.getApiClient(), ofStorages(c.getServiceLinks()), payload).then(result => assumeResultObject<StorageLocationApiResponse>(result, 'storageType'));
};

export const api_updateStorageLocation = (c: BackendClient, payload: StorageLocationUpdateDto): Promise<string> => {
  return apiPut(c.getApiClient(), ofStorages(c.getServiceLinks()), payload).then(result => assumeResultString(result));
};

export const api_deleteStorageLocation = (c: BackendClient, payload: StorageLocationDeleteDto): Promise<string> => {
  return apiDelete(c.getApiClient(), ofStorages(c.getServiceLinks()), payload).then(result => assumeResultString(result));
};

export const api_getActiveStorageTransfers = (c: BackendClient): Promise<StorageTransferDto[]> => {
  return apiGet(c.getApiClient(), ofStorageTransfers(c.getServiceLinks())).then(result => assumeResultArray<StorageTransferDto>(result));
};

export const api_getStorageTransferByDestination = (c: BackendClient, queryParams: GetStorageTransferQueryParams): Promise<StorageTransferDto > => {
  return apiGet(c.getApiClient(), ofStorageTransfers(c.getServiceLinks()), { queryParams }).then(result => assumeResultObject<StorageTransferDto>(result, 'requestedByUserId'));
};

export const api_createStorageTransfer = (c: BackendClient, payload: StorageTransferCreationDto): Promise<StorageTransferDto> => {
  return apiPost(c.getApiClient(), ofStorageTransfers(c.getServiceLinks()), payload).then(result => assumeResultObject<StorageTransferDto>(result, 'requestedByUserId'));
};

export const api_getPendingOperationsSummary = async (c: BackendClient, ignoreEmptyRows: boolean): Promise<TenantOpsSummaryReport[]> => {
  return apiGet(c.getApiClient(), ofPendingOperationsSummary(c.getServiceLinks()), { queryParams: { ignoreEmptyRows } }).then(result => assumeResultArray<TenantOpsSummaryReport>(result));
};

// these are super admin apis only used in e2e tests
export const api_housekeepingRun = (c: BackendClient): Promise<HousekeepingRunResponse> =>
  apiPost(c.getApiClient(), ofHousekeepingRun(c.getServiceLinks())).then(result => assumeResultObject<HousekeepingRunResponse>(result, 'taskId'));

export const api_housekeepingState = (c: BackendClient, taskId: string): Promise<HousekeepingTaskStateResponse> =>
  apiGet(c.getApiClient(), ofHousekeepingState(c.getServiceLinks(), taskId)).then(result => assumeResultObject<HousekeepingTaskStateResponse>(result, 'status'));

export const api_createRenderSequenceRequest = async (c: BackendClient, payload: RenderSequenceDto): Promise<RenderSequencePostResponseDto> =>
  await apiPost(c.getApiClient(), ofRenderSequenceRequests(c.getServiceLinks()), payload).then(result => assumeResultObject<RenderSequencePostResponseDto>(result, 'keldaRequestId'));

export const api_createItemRender = async (c: BackendClient, itemId: string, payload: ItemRenderDto): Promise<RenderSequencePostResponseDto> =>
  await apiPost(c.getApiClient(), ofItemRender(c.getServiceLinks(), itemId), payload).then(result => assumeResultObject<RenderSequencePostResponseDto>(result, 'keldaRequestId'));

export function api_getSecureApiKeys(c: BackendClient): Promise<SecureApiKeyDto[]> {
  return apiGet(c.getApiClient(), c.getServiceLinks().apiKeys).then(result => assumeResultArray<SecureApiKeyDto>(result));
}

export function api_postSecureApiKeys(c: BackendClient, req: SecureApiKeyModifyRequestDto): Promise<CreateSecureApiKeyResponseDto> {
  return apiPost(c.getApiClient(), c.getServiceLinks().apiKeys, req).then(result => assumeResultObject<CreateSecureApiKeyResponseDto>(result, 'apiKeyHeaderValue'));
}

export function api_putSecureApiKey(c: BackendClient, keyId: string, req: SecureApiKeyModifyRequestDto): Promise<SecureApiKeyDto> {
  return apiPut(c.getApiClient(), getPath(c.getServiceLinks().apiKey, { keyId }), req).then(result => assumeResultObject<SecureApiKeyDto>(result, 'keyId'));
}

export async function api_deleteSecureApiKey(c: BackendClient, keyId: string): Promise<void> {
  await apiDelete(c.getApiClient(), getPath(c.getServiceLinks().apiKey, { keyId }));
}

export async function api_getApiKeys(c: BackendClient, users: string[]): Promise<ApiKeysAdminDto> {
  return apiGet(c.getApiClient(), c.getServiceLinks().apiKeysAdmin, { queryParams: { userIds: users.join(',') } }).then(result => assumeResultObject<ApiKeysAdminDto>(result));
}

export async function api_updateApiKey(c: BackendClient, req: UpdateApiKeyPayload): Promise<SecureApiKeyDto> {
  return apiPut(c.getApiClient(), c.getServiceLinks().apiKeysAdmin, req).then(result => assumeResultObject<SecureApiKeyDto>(result, 'keyId'));
}

export async function api_deleteApiKey(c: BackendClient, keyId: string, userId: string): Promise<void> {
  await apiDelete(c.getApiClient(), c.getServiceLinks().apiKeysAdmin, undefined, { queryParams: { keyId, userId } });
}

export async function api_migrateMediaLocation(c: BackendClient, itemId: string, req: MigrateMediaRequestDto) {
  return apiPost(c.getApiClient(), getPath(c.getServiceLinks().migrateMediaLocation, { itemId }), req);
}

function removeUndefinedKeys(query: HttpQueryParameters): HttpQueryParameters {
  const r = { ...query };
  for (const key in r) {
    if (r[key] === undefined) {
      delete r[key];
    }
  }
  return r;
}

export async function api_getPlugins(c: BackendClient): Promise<PluginDto[]> {
  return apiGet(c.getApiClient(), c.getServiceLinks().plugins).then(result => assumeResultArray<PluginDto>(result));
}

export async function api_getPlugin(c: BackendClient, pluginId: string): Promise<PluginDto> {
  return apiGet(c.getApiClient(), getPath(c.getServiceLinks().plugin, { pluginId })).then(result => assumeResultObject<PluginDto>(result));
}

export async function api_createPlugin(c: BackendClient, dto: CreatePluginDto): Promise<PluginDto> {
  return apiPost(c.getApiClient(), c.getServiceLinks().plugins, dto).then(result => assumeResultObject<PluginDto>(result));
}

export async function api_updatePlugin(c: BackendClient, pluginId: string, dto: UpdatePluginDto): Promise<void> {
  await apiPut(c.getApiClient(), getPath(c.getServiceLinks().plugin, { id: pluginId }), dto);
}

export async function api_deletePlugin(c: BackendClient, pluginId: string): Promise<void> {
  await apiDelete(c.getApiClient(), getPath(c.getServiceLinks().plugin, { id: pluginId }));
}

export async function api_getItemOfflineStorageInfo(c: BackendClient, itemId: string): Promise<ItemOfflineStorageInfo> {
  const ret = await apiGet(c.getApiClient(), ofItemOfflineStorageInfo(c.getServiceLinks(), itemId)).then(result => assumeResultObjectOrNull<ItemOfflineStorageInfo>(result));
  return ret;
}

export async function api_getOfflineStoragePluginsForTenant(c: BackendClient): Promise<OfflineStoragePluginsDto> {
  return apiGet(c.getApiClient(), c.getServiceLinks().offlineStoragePlugins)
    .then(result => assumeResultObject<OfflineStoragePluginsDto>(result, 'plugins'));
}

export async function api_getOfflineStoragePluginsForIntegrator(c: BackendClient, integratorId: string): Promise<OfflineStoragePluginsDto> {
  const params: AdditionalParams = {queryParams: {integratorId}};
  return apiGet(c.getApiClient(), c.getServiceLinks().offlineStoragePlugins, params)
    .then(result => assumeResultObject<OfflineStoragePluginsDto>(result, 'plugins'));
}

export async function api_createOfflineStoragePlugin(c: BackendClient, dto: OfflineStoragePluginCreateDto): Promise<OfflineStoragePluginDto> {
  return apiPost(c.getApiClient(), c.getServiceLinks().offlineStoragePlugins, dto)
    .then(result => assumeResultObject<OfflineStoragePluginDto>(result, 'id'));
}

export async function api_updateOfflineStoragePlugin(c: BackendClient, pluginId: string, dto: OfflineStoragePluginUpdateDto): Promise<OfflineStoragePluginDto> {
  return apiPut(c.getApiClient(), getPath(c.getServiceLinks().offlineStoragePlugin, { pluginId }), dto)
    .then(result => assumeResultObject<OfflineStoragePluginDto>(result, 'id'));
}

